import type {
  Contact, ContactNote, Conversation, Message, TeamMember,
  AutomationFlow, AutomationRun, Campaign, CampaignRecipient,
  Template, AISettings, AITestEntry, SystemLog, SavedReply,
  BlacklistEntry, WorkspaceSettings,
  DeliveryState, LogLevel,
} from '../types';
import { supabase } from '@/integrations/supabase/client';
import { getCurrentWorkspaceId } from './workspaceState';

// ===================== CORE HELPERS =====================

function getCollection<T>(key: string): T[] {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : [];
  } catch { return []; }
}

/**
 * Quota-safe localStorage write. On QuotaExceededError, evicts the largest
 * non-essential caches (per-conversation messages, logs) and retries.
 */
export function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch (e: any) {
    if (e?.name !== 'QuotaExceededError' && e?.code !== 22) throw e;
    // Eviction strategy: drop largest disposable caches first
    try {
      // 1) Trim logs aggressively
      try {
        const logs = JSON.parse(localStorage.getItem('wf_logs') || '[]');
        if (Array.isArray(logs) && logs.length > 200) {
          localStorage.setItem('wf_logs', JSON.stringify(logs.slice(-200)));
        }
      } catch { localStorage.removeItem('wf_logs'); }

      // 2) Drop per-conversation message caches except the one being written
      const msgKeys: { k: string; size: number }[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith('wf_messages_') && k !== key) {
          msgKeys.push({ k, size: (localStorage.getItem(k) || '').length });
        }
      }
      // Remove largest first
      msgKeys.sort((a, b) => b.size - a.size);
      for (const { k } of msgKeys) {
        localStorage.removeItem(k);
        try { localStorage.setItem(key, value); return; } catch { /* keep evicting */ }
      }

      // 3) Last resort: clear automation runs / notifications / ai history
      ['wf_automation_runs', 'wf_notifications', 'wf_ai_history', 'wf_logs'].forEach(k => {
        if (k !== key) localStorage.removeItem(k);
      });
      localStorage.setItem(key, value);
    } catch (e2) {
      console.warn('[storage] quota eviction failed', e2);
    }
  }
}

function setCollection<T>(key: string, data: T[]): void {
  safeSetItem(key, JSON.stringify(data));
}

function getDocument<T>(key: string): T | null {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch { return null; }
}

function setDocument<T>(key: string, data: T): void {
  safeSetItem(key, JSON.stringify(data));
}

export function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
}

function normalizePhoneIdentity(phone: string): string {
  return phone.replace(/\D/g, '');
}

// ===================== EVENT SYSTEM =====================

type Listener = () => void;
const listeners: Record<string, Listener[]> = {};

function notify(key: string) {
  (listeners[key] || []).forEach(fn => fn());
}

/** Exported for whatsappService to trigger message re-renders */
export function _notifyKey(key: string) {
  notify(key);
}

function subscribe(key: string, fn: Listener): () => void {
  if (!listeners[key]) listeners[key] = [];
  listeners[key].push(fn);
  return () => { listeners[key] = listeners[key].filter(l => l !== fn); };
}

// ===================== LOG SERVICE (must come first, used by others) =====================

export interface LogFilters {
  module?: string;
  action?: string;
  level?: string;
  entityType?: string;
  search?: string;
  dateRange?: { start: Date; end: Date };
  sortBy?: 'newest' | 'oldest' | 'level' | 'module';
  limit?: number;
  offset?: number;
}

const LEVEL_ORDER: Record<string, number> = { error: 0, warn: 1, success: 2, info: 3 };

export const logService = {
  add: (module: string, action: string, message: string, metadata: Record<string, any> = {}, level: LogLevel = 'info', entityType?: string, entityId?: string): string => {
    const logs = getCollection<SystemLog>('wf_logs');
    const entry: SystemLog = {
      id: generateId(),
      module, action, entityType, entityId,
      level, message, metadata,
      createdAt: new Date().toISOString(),
    };
    logs.push(entry);
    setCollection('wf_logs', logs.slice(-2000));
    notify('wf_logs');
    void supabase.from('system_logs' as any).upsert({ id: entry.id, module, action, entity_type: entityType || null, entity_id: entityId || null, level, message, metadata, created_at: entry.createdAt, workspace_id: getCurrentWorkspaceId() } as any, { onConflict: 'id' });
    return entry.id;
  },

  subscribe: (callback: (logs: SystemLog[]) => void) => {
    const handler = () => callback(
      getCollection<SystemLog>('wf_logs')
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    );
    handler();
    return subscribe('wf_logs', handler);
  },

  getAll: (): SystemLog[] => getCollection<SystemLog>('wf_logs'),

  getFiltered: (filters: LogFilters): { logs: SystemLog[]; total: number } => {
    let result = getCollection<SystemLog>('wf_logs');

    if (filters.module && filters.module !== 'all') {
      result = result.filter(l => l.module === filters.module);
    }
    if (filters.action && filters.action !== 'all') {
      result = result.filter(l => l.action === filters.action);
    }
    if (filters.level && filters.level !== 'all') {
      result = result.filter(l => l.level === filters.level);
    }
    if (filters.entityType && filters.entityType !== 'all') {
      result = result.filter(l => l.entityType === filters.entityType);
    }
    if (filters.search) {
      const q = filters.search.toLowerCase();
      result = result.filter(l =>
        l.message?.toLowerCase().includes(q) ||
        l.action?.toLowerCase().includes(q) ||
        (l.metadata && JSON.stringify(l.metadata).toLowerCase().includes(q))
      );
    }
    if (filters.dateRange) {
      result = result.filter(l => {
        const d = new Date(l.createdAt);
        return d >= filters.dateRange!.start && d <= filters.dateRange!.end;
      });
    }

    const total = result.length;

    // Sort
    const sortBy = filters.sortBy || 'newest';
    result.sort((a, b) => {
      switch (sortBy) {
        case 'oldest': return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case 'level': return (LEVEL_ORDER[a.level] ?? 3) - (LEVEL_ORDER[b.level] ?? 3);
        case 'module': return a.module.localeCompare(b.module);
        default: return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
    });

    // Paginate
    if (filters.offset) result = result.slice(filters.offset);
    if (filters.limit) result = result.slice(0, filters.limit);

    return { logs: result, total };
  },

  getRecent: (limit = 10): SystemLog[] => {
    return getCollection<SystemLog>('wf_logs')
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, limit);
  },

  getDistinctActions: (): string[] => {
    const actions = new Set<string>();
    getCollection<SystemLog>('wf_logs').forEach(l => { if (l.action) actions.add(l.action); });
    return Array.from(actions).sort();
  },

  getDistinctEntityTypes: (): string[] => {
    const types = new Set<string>();
    getCollection<SystemLog>('wf_logs').forEach(l => { if (l.entityType) types.add(l.entityType); });
    return Array.from(types).sort();
  },

  getByModule: (module: string): SystemLog[] =>
    getCollection<SystemLog>('wf_logs').filter(l => l.module === module),

  getByDateRange: (start: Date, end: Date): SystemLog[] =>
    getCollection<SystemLog>('wf_logs').filter(l => {
      const d = new Date(l.createdAt);
      return d >= start && d <= end;
    }),

  getStats: () => {
    const logs = getCollection<SystemLog>('wf_logs');
    const byLevel: Record<string, number> = {};
    const byModule: Record<string, number> = {};
    logs.forEach(l => {
      byLevel[l.level] = (byLevel[l.level] || 0) + 1;
      byModule[l.module] = (byModule[l.module] || 0) + 1;
    });
    return { total: logs.length, byLevel, byModule };
  },

  clear: (filters?: LogFilters) => {
    if (!filters || (!filters.module && !filters.level && !filters.search)) {
      setCollection('wf_logs', []);
    } else {
      const { logs: toRemove } = logService.getFiltered(filters);
      const removeIds = new Set(toRemove.map(l => l.id));
      const remaining = getCollection<SystemLog>('wf_logs').filter(l => !removeIds.has(l.id));
      setCollection('wf_logs', remaining);
    }
    notify('wf_logs');
  },
};

// ===================== CONTACT SERVICE =====================

export const contactService = {
  subscribe: (callback: (contacts: Contact[]) => void) => {
    const handler = () => callback(
      getCollection<Contact>('wf_contacts')
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    );
    handler();
    return subscribe('wf_contacts', handler);
  },

  getAll: (): Contact[] => getCollection<Contact>('wf_contacts'),

  getById: (id: string): Contact | null =>
    getCollection<Contact>('wf_contacts').find(c => c.id === id) || null,

  getByPhone: (phone: string): Contact | null => {
    const normalized = normalizePhoneIdentity(phone);
    return getCollection<Contact>('wf_contacts').find(c => normalizePhoneIdentity(c.phoneNumber) === normalized) || null;
  },

  create: (data: Partial<Contact>, id?: string): string => {
    const contacts = getCollection<Contact>('wf_contacts');
    const now = new Date().toISOString();
    const normalizedPhone = normalizePhoneIdentity(data.phoneNumber || '');
    const contact: Contact = {
      id: id || generateId(),
      fullName: data.fullName || '',
      phoneNumber: normalizedPhone,
      email: data.email,
      city: data.city,
      country: data.country || undefined,
      language: data.language || undefined,
      tags: data.tags || [],
      leadStatus: data.leadStatus || 'new',
      assignedAgentId: data.assignedAgentId,
      source: data.source || 'Manual',
      notesCount: data.notesCount || 0,
      lastContactAt: data.lastContactAt,
      lastMessageAt: data.lastMessageAt,
      createdAt: data.createdAt || now,
      updatedAt: now,
      productName: data.productName,
      orderAmount: data.orderAmount,
      orderStatus: data.orderStatus,
      deliveryStatus: data.deliveryStatus,
      codConfirmationStatus: data.codConfirmationStatus,
      currency: data.currency || undefined,
      orderCreatedAt: data.orderCreatedAt,
      lastOrderUpdateAt: data.lastOrderUpdateAt,
    };
    contacts.push(contact);
    setCollection('wf_contacts', contacts);
    notify('wf_contacts');
    logService.add('contacts', 'create', `Contact "${contact.fullName}" created`, { contactId: contact.id }, 'success', 'contact', contact.id);
    return contact.id;
  },

  update: (id: string, data: Partial<Contact>) => {
    const contacts = getCollection<Contact>('wf_contacts');
    const idx = contacts.findIndex(c => c.id === id);
    if (idx === -1) return;
    const now = new Date().toISOString();
    contacts[idx] = { ...contacts[idx], ...data, updatedAt: now };
    setCollection('wf_contacts', contacts);
    notify('wf_contacts');

    // Persist to DB so changes survive refresh
    const dbPatch: Record<string, any> = { updated_at: now };
    if (data.fullName !== undefined) dbPatch.full_name = data.fullName;
    if (data.phoneNumber !== undefined) dbPatch.phone_number = normalizePhoneIdentity(data.phoneNumber);
    if (data.email !== undefined) dbPatch.email = data.email;
    if (data.city !== undefined) dbPatch.city = data.city;
    if (data.country !== undefined) dbPatch.country = data.country;
    if (data.language !== undefined) dbPatch.language = data.language;
    if (data.tags !== undefined) dbPatch.tags = data.tags;
    if (data.leadStatus !== undefined) dbPatch.lead_status = data.leadStatus;
    if (data.assignedAgentId !== undefined) dbPatch.assigned_agent_id = data.assignedAgentId;
    if (data.source !== undefined) dbPatch.source = data.source;
    if (data.notesCount !== undefined) dbPatch.notes_count = data.notesCount;
    if (data.lastContactAt !== undefined) dbPatch.last_contact_at = data.lastContactAt;
    if (data.lastMessageAt !== undefined) dbPatch.last_message_at = data.lastMessageAt;
    if (data.productName !== undefined) dbPatch.product_name = data.productName;
    if (data.orderAmount !== undefined) dbPatch.order_amount = data.orderAmount;
    if (data.orderStatus !== undefined) dbPatch.order_status = data.orderStatus;
    if (data.deliveryStatus !== undefined) dbPatch.delivery_status = data.deliveryStatus;
    if (data.codConfirmationStatus !== undefined) dbPatch.cod_confirmation_status = data.codConfirmationStatus;
    if (data.currency !== undefined) dbPatch.currency = data.currency;
    if (data.orderCreatedAt !== undefined) dbPatch.order_created_at = data.orderCreatedAt;
    if (data.lastOrderUpdateAt !== undefined) dbPatch.last_order_update_at = data.lastOrderUpdateAt;

    void supabase.from('contacts').update(dbPatch as any).eq('id', id).then(({ error }) => {
      if (error) console.error('[contactService.update] DB persist failed:', error.message, { id, patch: dbPatch });
    });
  },

  delete: (id: string) => {
    const contact = contactService.getById(id);
    const contacts = getCollection<Contact>('wf_contacts').filter(c => c.id !== id);
    setCollection('wf_contacts', contacts);
    // Clean up related data
    const conversations = getCollection<Conversation>('wf_conversations').filter(c => c.contactId === id);
    conversations.forEach(conv => {
      localStorage.removeItem(`wf_messages_${conv.id}`);
    });
    setCollection('wf_conversations', getCollection<Conversation>('wf_conversations').filter(c => c.contactId !== id));
    localStorage.removeItem(`wf_notes_${id}`);
    notify('wf_contacts');
    notify('wf_conversations');
    logService.add('contacts', 'delete', `Contact "${contact?.fullName || id}" deleted`, { contactId: id }, 'info', 'contact', id);
  },

  bulkUpdate: (ids: string[], data: Partial<Contact>) => {
    ids.forEach(id => contactService.update(id, data));
  },

  count: (): number => getCollection<Contact>('wf_contacts').length,
};

// ===================== CONVERSATION SERVICE =====================

export const conversationService = {
  subscribe: (callback: (conversations: Conversation[]) => void) => {
    const handler = () => callback(
      getCollection<Conversation>('wf_conversations')
        .sort((a, b) => {
          const tA = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : new Date(a.createdAt).getTime();
          const tB = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : new Date(b.createdAt).getTime();
          return tB - tA;
        })
    );
    handler();
    return subscribe('wf_conversations', handler);
  },

  getAll: (): Conversation[] => getCollection<Conversation>('wf_conversations'),

  getById: (id: string): Conversation | null =>
    getCollection<Conversation>('wf_conversations').find(c => c.id === id) || null,

  getByContactId: (contactId: string): Conversation | null =>
    getCollection<Conversation>('wf_conversations').find(c => c.contactId === contactId) || null,

  create: (data: Partial<Conversation>): string => {
    const conversations = getCollection<Conversation>('wf_conversations');
    const now = new Date().toISOString();
    const conv: Conversation = {
      id: data.id || generateId(),
      contactId: data.contactId || '',
      channelType: data.channelType || 'whatsapp_local',
      status: data.status || 'open',
      assignedAgentId: data.assignedAgentId,
      unreadCount: data.unreadCount || 0,
      tags: data.tags || [],
      lastMessagePreview: data.lastMessagePreview,
      lastMessageAt: data.lastMessageAt,
      createdAt: data.createdAt || now,
      updatedAt: now,
    };
    conversations.push(conv);
    setCollection('wf_conversations', conversations);
    notify('wf_conversations');
    return conv.id;
  },

  update: (id: string, data: Partial<Conversation>) => {
    const conversations = getCollection<Conversation>('wf_conversations');
    const idx = conversations.findIndex(c => c.id === id);
    if (idx !== -1) {
      conversations[idx] = { ...conversations[idx], ...data, updatedAt: new Date().toISOString() };
      setCollection('wf_conversations', conversations);
      notify('wf_conversations');
    }
  },

  markAsRead: (id: string) => {
    conversationService.update(id, { unreadCount: 0 });
    // Persist to DB so it survives refresh/other browsers
    supabase.from('conversations').update({ unread_count: 0, updated_at: new Date().toISOString() }).eq('id', id).then(({ error }) => {
      if (error) console.error('[markAsRead] DB update failed:', error.message);
    });
  },

  markAsUnread: (id: string) => {
    const current = conversationService.getById(id);
    const next = Math.max(1, (current?.unreadCount || 0) || 1);
    conversationService.update(id, { unreadCount: next });
    supabase.from('conversations').update({ unread_count: next, updated_at: new Date().toISOString() }).eq('id', id).then(({ error }) => {
      if (error) console.error('[markAsUnread] DB update failed:', error.message);
    });
  },

  setPinned: (id: string, pinned: boolean) => {
    conversationService.update(id, { pinned });
    supabase.from('conversations').update({ pinned, updated_at: new Date().toISOString() }).eq('id', id).then(({ error }) => {
      if (error) console.error('[setPinned] DB update failed:', error.message);
    });
  },

  delete: (id: string) => {
    const conversations = getCollection<Conversation>('wf_conversations');
    setCollection('wf_conversations', conversations.filter(c => c.id !== id));
    notify('wf_conversations');
    // Also clear local messages cache for this conversation
    try { localStorage.removeItem(`wf_messages_${id}`); } catch {}
    notify(`wf_messages_${id}`);
    void supabase.from('conversations').delete().eq('id', id).then(({ error }) => {
      if (error) console.error('[deleteConversation] DB delete failed:', error.message);
    });
  },

  getOrCreateForContact: (contactId: string): string => {
    const existing = conversationService.getByContactId(contactId);
    if (existing) return existing.id;
    return conversationService.create({ contactId, status: 'open' });
  },

  countByStatus: (status?: string): number => {
    const all = getCollection<Conversation>('wf_conversations');
    if (!status) return all.length;
    return all.filter(c => c.status === status).length;
  },

  countWithUnread: (): number =>
    getCollection<Conversation>('wf_conversations').filter(c => c.unreadCount > 0).length,
};

// ===================== MESSAGE SERVICE =====================

export const messageService = {
  subscribe: (conversationId: string, callback: (messages: Message[]) => void) => {
    const key = `wf_messages_${conversationId}`;
    const handler = () => {
      const msgs = getCollection<Message>(key);
      // Dedup by externalMessageId before rendering
      const seen = new Set<string>();
      const deduped = msgs.filter(m => {
        if (m.externalMessageId) {
          if (seen.has(m.externalMessageId)) return false;
          seen.add(m.externalMessageId);
        }
        return true;
      });
      // Order: whatsappTimestamp first, then createdAt
      deduped.sort((a, b) => {
        const tA = (a as any).whatsappTimestamp ? new Date((a as any).whatsappTimestamp).getTime() : new Date(a.createdAt).getTime();
        const tB = (b as any).whatsappTimestamp ? new Date((b as any).whatsappTimestamp).getTime() : new Date(b.createdAt).getTime();
        return tA - tB;
      });
      callback(deduped);
    };
    handler();
    return subscribe(key, handler);
  },

  getByConversation: (conversationId: string): Message[] => {
    const msgs = getCollection<Message>(`wf_messages_${conversationId}`);
    const seen = new Set<string>();
    return msgs.filter(m => {
      if (m.externalMessageId) {
        if (seen.has(m.externalMessageId)) return false;
        seen.add(m.externalMessageId);
      }
      return true;
    }).sort((a, b) => {
      const tA = (a as any).whatsappTimestamp ? new Date((a as any).whatsappTimestamp).getTime() : new Date(a.createdAt).getTime();
      const tB = (b as any).whatsappTimestamp ? new Date((b as any).whatsappTimestamp).getTime() : new Date(b.createdAt).getTime();
      return tA - tB;
    });
  },

  getAllOutbound: (): { count: number; messages: Message[] } => {
    const conversations = getCollection<Conversation>('wf_conversations');
    let all: Message[] = [];
    conversations.forEach(conv => {
      const msgs = getCollection<Message>(`wf_messages_${conv.id}`);
      all = all.concat(msgs.filter(m => m.direction === 'outbound'));
    });
    return { count: all.length, messages: all };
  },

  getAllMessages: (): Message[] => {
    const conversations = getCollection<Conversation>('wf_conversations');
    let all: Message[] = [];
    conversations.forEach(conv => {
      all = all.concat(getCollection<Message>(`wf_messages_${conv.id}`));
    });
    return all;
  },

  countOutboundToday: (): number => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const conversations = getCollection<Conversation>('wf_conversations');
    let count = 0;
    conversations.forEach(conv => {
      const msgs = getCollection<Message>(`wf_messages_${conv.id}`);
      count += msgs.filter(m => m.direction === 'outbound' && new Date(m.createdAt) >= today).length;
    });
    return count;
  },

  countByDateRange: (start: Date, end: Date): { inbound: number; outbound: number; total: number } => {
    const conversations = getCollection<Conversation>('wf_conversations');
    let inbound = 0, outbound = 0;
    conversations.forEach(conv => {
      const msgs = getCollection<Message>(`wf_messages_${conv.id}`);
      msgs.forEach(m => {
        const d = new Date(m.createdAt);
        if (d >= start && d <= end) {
          if (m.direction === 'inbound') inbound++;
          else if (m.direction === 'outbound') outbound++;
        }
      });
    });
    return { inbound, outbound, total: inbound + outbound };
  },

  countByDeliveryState: (): Record<DeliveryState, number> => {
    const conversations = getCollection<Conversation>('wf_conversations');
    const counts: Record<string, number> = { pending: 0, sent: 0, delivered: 0, read: 0, failed: 0, local_only: 0 };
    conversations.forEach(conv => {
      const msgs = getCollection<Message>(`wf_messages_${conv.id}`);
      msgs.filter(m => m.direction === 'outbound').forEach(m => {
        counts[m.deliveryState] = (counts[m.deliveryState] || 0) + 1;
      });
    });
    return counts as Record<DeliveryState, number>;
  },

  create: (data: {
    conversationId: string;
    contactId: string;
    direction: Message['direction'];
    messageType?: Message['messageType'];
    body: string;
    deliveryState?: DeliveryState;
    sentBy?: string;
    templateId?: string;
    campaignId?: string;
    automationId?: string;
    externalMessageId?: string;
    mediaUrl?: string;
    contextWamid?: string;
    createdAt?: string;
  }): Message => {
    const key = `wf_messages_${data.conversationId}`;
    const messages = getCollection<Message>(key);
    const now = data.createdAt || new Date().toISOString();
    const msg: Message = {
      id: generateId(),
      conversationId: data.conversationId,
      contactId: data.contactId,
      direction: data.direction,
      messageType: data.messageType || 'text',
      body: data.body,
      deliveryState: data.deliveryState || (data.direction === 'outbound' ? 'local_only' : 'delivered'),
      sentBy: data.sentBy,
      templateId: data.templateId,
      campaignId: data.campaignId,
      automationId: data.automationId,
      externalMessageId: data.externalMessageId,
      mediaUrl: data.mediaUrl,
      contextWamid: data.contextWamid,
      createdAt: now,
      updatedAt: now,
    };
    messages.push(msg);
    setCollection(key, messages);
    notify(key);

    // Update conversation
    conversationService.update(data.conversationId, {
      lastMessagePreview: data.body.substring(0, 100),
      lastMessageAt: now,
      ...(data.direction === 'inbound' ? {
        unreadCount: (conversationService.getById(data.conversationId)?.unreadCount || 0) + 1,
      } : {}),
    });

    // Update contact
    contactService.update(data.contactId, {
      lastMessageAt: now,
      lastContactAt: now,
    });

    return msg;
  },

  sendLocal: (conversationId: string, contactId: string, text: string, sentBy?: string): Message => {
    const msg = messageService.create({
      conversationId, contactId,
      direction: 'outbound',
      messageType: 'text',
      body: text,
      deliveryState: 'local_only',
      sentBy: sentBy || 'agent',
    });
    logService.add('inbox', 'message_sent', `Message sent to ${contactService.getById(contactId)?.fullName || contactId}`, {
      conversationId, contactId, messageId: msg.id,
    }, 'info', 'message', msg.id);
    return msg;
  },

  receiveLocal: (conversationId: string, contactId: string, text: string): Message => {
    const msg = messageService.create({
      conversationId, contactId,
      direction: 'inbound',
      messageType: 'text',
      body: text,
      deliveryState: 'delivered',
    });
    logService.add('inbox', 'message_received', `Message received from ${contactService.getById(contactId)?.fullName || contactId}`, {
      conversationId, contactId, messageId: msg.id,
    }, 'info', 'message', msg.id);
    return msg;
  },

  sendTemplateMessage: (conversationId: string, contactId: string, templateId: string, body: string, campaignId?: string): Message => {
    const msg = messageService.create({
      conversationId, contactId,
      direction: 'outbound',
      messageType: 'template',
      body,
      deliveryState: 'local_only',
      templateId,
      campaignId,
    });
    // Increment template usage
    const templates = getCollection<Template>('wf_templates');
    const tIdx = templates.findIndex(t => t.id === templateId);
    if (tIdx !== -1) {
      templates[tIdx].usageCount = (templates[tIdx].usageCount || 0) + 1;
      setCollection('wf_templates', templates);
      notify('wf_templates');
    }
    return msg;
  },

  sendSystemMessage: (conversationId: string, contactId: string, body: string, automationId?: string): Message => {
    return messageService.create({
      conversationId, contactId,
      direction: 'system',
      messageType: 'system',
      body,
      deliveryState: 'delivered',
      automationId,
    });
  },
};

// ===================== NOTE SERVICE =====================

export const noteService = {
  subscribe: (contactId: string, callback: (notes: ContactNote[]) => void) => {
    const key = `wf_notes_${contactId}`;
    const handler = () => callback(
      getCollection<ContactNote>(key).sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )
    );
    handler();
    return subscribe(key, handler);
  },

  create: (contactId: string, body: string, createdBy?: string): string => {
    const key = `wf_notes_${contactId}`;
    const notes = getCollection<ContactNote>(key);
    const now = new Date().toISOString();
    const note: ContactNote = { id: generateId(), contactId, body, createdBy, createdAt: now, updatedAt: now };
    notes.push(note);
    setCollection(key, notes);
    notify(key);
    contactService.update(contactId, { notesCount: notes.length });
    void supabase.from('contact_notes' as any).upsert({ id: note.id, contact_id: contactId, body: note.body, created_by: note.createdBy || null, created_at: note.createdAt, updated_at: note.updatedAt } as any, { onConflict: 'id' });
    logService.add('contacts', 'note_created', `Note added for contact`, { contactId }, 'info', 'note', note.id);
    return note.id;
  },

  update: (contactId: string, noteId: string, body: string) => {
    const key = `wf_notes_${contactId}`;
    const notes = getCollection<ContactNote>(key);
    const idx = notes.findIndex(n => n.id === noteId);
    if (idx !== -1) {
      notes[idx] = { ...notes[idx], body, updatedAt: new Date().toISOString() };
      setCollection(key, notes);
      notify(key);
      void supabase.from('contact_notes' as any).upsert({ id: noteId, contact_id: contactId, body, updated_at: notes[idx].updatedAt } as any, { onConflict: 'id' });
      logService.add('contacts', 'note_updated', `Note updated`, { contactId, noteId }, 'info', 'note', noteId);
    }
  },

  delete: (contactId: string, noteId: string) => {
    const key = `wf_notes_${contactId}`;
    const notes = getCollection<ContactNote>(key).filter(n => n.id !== noteId);
    setCollection(key, notes);
    notify(key);
    contactService.update(contactId, { notesCount: notes.length });
    void supabase.from('contact_notes' as any).delete().eq('id', noteId);
    logService.add('contacts', 'note_deleted', `Note deleted`, { contactId, noteId }, 'info', 'note', noteId);
  },

  getCount: (contactId: string): number =>
    getCollection<ContactNote>(`wf_notes_${contactId}`).length,
};

// ===================== AUTOMATION SERVICES (DB-BACKED) =====================
// Re-exported from automationDbService — persisted in Lovable Cloud
import { automationDbService as _automationDbService, automationRunDbService as _automationRunDbService } from './automationDbService';
export const automationService = _automationDbService;
export const automationRunService = _automationRunDbService;



// ===================== CAMPAIGN SERVICE =====================

function buildCampaignRecord(data: Partial<Campaign>): Campaign {
  const now = new Date().toISOString();
  return {
    id: data.id || generateId(),
    name: data.name || 'New Campaign',
    type: data.type || 'broadcast',
    status: data.status || 'draft',
    templateId: data.templateId,
    audienceFilters: data.audienceFilters,
    audienceCount: data.audienceCount,
    scheduledAt: data.scheduledAt,
    startedAt: data.startedAt,
    finishedAt: data.finishedAt,
    totalRecipients: data.totalRecipients || 0,
    sentCount: data.sentCount || 0,
    deliveredCount: data.deliveredCount || 0,
    readCount: data.readCount || 0,
    failedCount: data.failedCount || 0,
    repliedCount: data.repliedCount || 0,
    errorReason: data.errorReason,
    createdAt: data.createdAt || now,
    updatedAt: now,
  };
}

function campaignToDbRow(campaign: Campaign, includeCreatedAt: boolean) {
  const row: Record<string, any> = {
    id: campaign.id,
    name: campaign.name,
    type: campaign.type,
    status: campaign.status,
    template_id: campaign.templateId || null,
    audience_filters: campaign.audienceFilters || {},
    audience_count: campaign.audienceCount || 0,
    scheduled_at: campaign.scheduledAt || null,
    started_at: campaign.startedAt || null,
    finished_at: campaign.finishedAt || null,
    total_recipients: campaign.totalRecipients,
    sent_count: campaign.sentCount,
    delivered_count: campaign.deliveredCount,
    read_count: campaign.readCount || 0,
    failed_count: campaign.failedCount,
    replied_count: campaign.repliedCount,
    error_reason: campaign.errorReason || null,
    updated_at: campaign.updatedAt,
    workspace_id: getCurrentWorkspaceId(),
  };
  if (includeCreatedAt) row.created_at = campaign.createdAt;
  return row;
}

function saveCampaignLocal(campaign: Campaign) {
  const campaigns = getCollection<Campaign>('wf_campaigns');
  const idx = campaigns.findIndex(c => c.id === campaign.id);
  if (idx === -1) campaigns.push(campaign);
  else campaigns[idx] = campaign;
  setCollection('wf_campaigns', campaigns);
  notify('wf_campaigns');
}

async function persistCampaign(campaign: Campaign, mode: 'insert' | 'update' | 'upsert') {
  const row = campaignToDbRow(campaign, mode !== 'update');
  const table = supabase.from('campaigns' as any);
  const result = mode === 'insert'
    ? await table.insert(row as any)
    : mode === 'update'
      ? await table.update(row as any).eq('id', campaign.id).eq('workspace_id', getCurrentWorkspaceId())
      : await table.upsert(row as any, { onConflict: 'id' });

  if (result.error) throw result.error;
}

export const campaignService = {
  subscribe: (callback: (campaigns: Campaign[]) => void) => {
    const handler = () => callback(
      getCollection<Campaign>('wf_campaigns')
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    );
    handler();
    return subscribe('wf_campaigns', handler);
  },

  getAll: (): Campaign[] => getCollection<Campaign>('wf_campaigns'),

  getById: (id: string): Campaign | null =>
    getCollection<Campaign>('wf_campaigns').find(c => c.id === id) || null,

  create: (data: Partial<Campaign>): string => {
    const campaign = buildCampaignRecord(data);
    saveCampaignLocal(campaign);
    void persistCampaign(campaign, 'insert').then(() => {
      logService.add('campaigns', 'create', `Campaign "${campaign.name}" created`, { campaignId: campaign.id }, 'success', 'campaign', campaign.id);
    }).catch((error) => {
      console.error('[campaignService.create] DB persist failed:', error.message, { campaignId: campaign.id, workspaceId: getCurrentWorkspaceId() });
      setCollection('wf_campaigns', getCollection<Campaign>('wf_campaigns').filter(c => c.id !== campaign.id));
      notify('wf_campaigns');
      logService.add('campaigns', 'create_failed', `Campaign "${campaign.name}" was not saved: ${error.message}`, { campaignId: campaign.id }, 'error', 'campaign', campaign.id);
    });
    return campaign.id;
  },

  createAsync: async (data: Partial<Campaign>): Promise<string> => {
    const campaign = buildCampaignRecord(data);
    await persistCampaign(campaign, 'insert');
    saveCampaignLocal(campaign);
    logService.add('campaigns', 'create', `Campaign "${campaign.name}" created`, { campaignId: campaign.id }, 'success', 'campaign', campaign.id);
    return campaign.id;
  },

  update: (id: string, data: Partial<Campaign>) => {
    const campaigns = getCollection<Campaign>('wf_campaigns');
    const idx = campaigns.findIndex(c => c.id === id);
    if (idx !== -1) {
      campaigns[idx] = { ...campaigns[idx], ...data, updatedAt: new Date().toISOString() };
      setCollection('wf_campaigns', campaigns);
      notify('wf_campaigns');

      const c = campaigns[idx];
      void persistCampaign(c, 'update').catch((error) => {
        console.error('[campaignService.update] DB persist failed:', error.message, { campaignId: c.id, workspaceId: getCurrentWorkspaceId() });
        logService.add('campaigns', 'update_failed', `Campaign "${c.name}" was not saved: ${error.message}`, { campaignId: c.id }, 'error', 'campaign', c.id);
      });
    }
  },

  updateAsync: async (id: string, data: Partial<Campaign>): Promise<void> => {
    const campaigns = getCollection<Campaign>('wf_campaigns');
    const idx = campaigns.findIndex(c => c.id === id);
    if (idx === -1) throw new Error('Campaign not found');
    const campaign = { ...campaigns[idx], ...data, updatedAt: new Date().toISOString() };
    await persistCampaign(campaign, 'update');
    campaigns[idx] = campaign;
    setCollection('wf_campaigns', campaigns);
    notify('wf_campaigns');
  },

  delete: async (id: string) => {
    const campaign = campaignService.getById(id);
    setCollection('wf_campaigns', getCollection<Campaign>('wf_campaigns').filter(c => c.id !== id));
    localStorage.removeItem(`wf_campaign_recipients_${id}`);
    notify('wf_campaigns');
    logService.add('campaigns', 'delete', `Campaign "${campaign?.name || id}" deleted`, { campaignId: id }, 'info', 'campaign', id);
    // Also delete from DB
    try {
      await supabase.from('campaign_recipients').delete().eq('campaign_id', id);
      await supabase.from('campaigns').delete().eq('id', id);
    } catch (e) {
      console.error('campaign_db_delete_error', e);
    }
  },

  recalculateStats: (campaignId: string) => {
    const recipients = getCollection<CampaignRecipient>(`wf_campaign_recipients_${campaignId}`);
    const total = recipients.length;
    const sent = recipients.filter(r => r.status !== 'pending' && r.status !== 'skipped').length;
    const delivered = recipients.filter(r => r.status === 'delivered' || r.status === 'read' || r.status === 'replied').length;
    const read = recipients.filter(r => r.status === 'read' || r.status === 'replied').length;
    const failed = recipients.filter(r => r.status === 'failed').length;
    const replied = recipients.filter(r => r.status === 'replied').length;
    campaignService.update(campaignId, { totalRecipients: total, sentCount: sent, deliveredCount: delivered, readCount: read, failedCount: failed, repliedCount: replied });
  },
};

// ===================== CAMPAIGN RECIPIENT SERVICE =====================

export const campaignRecipientService = {
  subscribe: (campaignId: string, callback: (recipients: CampaignRecipient[]) => void) => {
    const key = `wf_campaign_recipients_${campaignId}`;
    const handler = () => callback(getCollection<CampaignRecipient>(key));
    handler();
    return subscribe(key, handler);
  },

  getAll: (campaignId: string): CampaignRecipient[] =>
    getCollection<CampaignRecipient>(`wf_campaign_recipients_${campaignId}`),

  create: (data: Partial<CampaignRecipient>): string => {
    const key = `wf_campaign_recipients_${data.campaignId}`;
    const recipients = getCollection<CampaignRecipient>(key);
    const item: CampaignRecipient = {
      id: generateId(),
      campaignId: data.campaignId || '',
      contactId: data.contactId || '',
      conversationId: data.conversationId,
      status: data.status || 'pending',
      errorReason: data.errorReason,
      sentAt: data.sentAt,
      deliveredAt: data.deliveredAt,
      readAt: data.readAt,
      repliedAt: data.repliedAt,
      createdAt: data.createdAt || new Date().toISOString(),
    };
    recipients.push(item);
    setCollection(key, recipients);
    notify(key);
    void supabase.from('campaign_recipients' as any).upsert({ id: item.id, campaign_id: item.campaignId, contact_id: item.contactId, conversation_id: item.conversationId || null, status: item.status, error_reason: item.errorReason || null, sent_at: item.sentAt || null, delivered_at: item.deliveredAt || null, read_at: item.readAt || null, replied_at: item.repliedAt || null, created_at: item.createdAt, workspace_id: getCurrentWorkspaceId() } as any, { onConflict: 'id' });
    return item.id;
  },

  update: (campaignId: string, recipientId: string, data: Partial<CampaignRecipient>) => {
    const key = `wf_campaign_recipients_${campaignId}`;
    const recipients = getCollection<CampaignRecipient>(key);
    const idx = recipients.findIndex(r => r.id === recipientId);
    if (idx !== -1) {
      recipients[idx] = { ...recipients[idx], ...data };
      setCollection(key, recipients);
      notify(key);
      const r = recipients[idx];
      void supabase.from('campaign_recipients' as any).upsert({ id: r.id, campaign_id: r.campaignId, contact_id: r.contactId, conversation_id: r.conversationId || null, status: r.status, error_reason: r.errorReason || null, sent_at: r.sentAt || null, delivered_at: r.deliveredAt || null, read_at: r.readAt || null, replied_at: r.repliedAt || null, workspace_id: getCurrentWorkspaceId() } as any, { onConflict: 'id' });
    }
  },
};

// ===================== TEMPLATE SERVICE =====================

function _upsertTemplateToDb(t: Template) {
  void supabase.from('templates' as any).upsert({
    id: t.id, name: t.name, category: t.category, language: t.language,
    header_type: t.headerType, header_text: t.headerText || null,
    header_media_url: t.headerMediaUrl || null, body_text: t.bodyText,
    footer_text: t.footerText || null, buttons: t.buttons || [],
    variables: t.variables || [], status: t.status,
    meta_template_id: t.metaTemplateId || null, meta_template_name: t.metaTemplateName || null,
    preview_sample_data: t.previewSampleData || null,
    quality_score: t.qualityScore || null, rejection_reason: t.rejectionReason || null,
    submitted_at: t.submittedAt || null, usage_count: t.usageCount || 0,
    created_at: t.createdAt, updated_at: t.updatedAt,
  } as any, { onConflict: 'id' });
}

export const templateService = {
  subscribe: (callback: (templates: Template[]) => void) => {
    const handler = () => callback(getCollection<Template>('wf_templates'));
    handler();
    return subscribe('wf_templates', handler);
  },

  subscribeFiltered: (filters: { status?: string; category?: string; language?: string }, callback: (templates: Template[]) => void) => {
    const handler = () => {
      let templates = getCollection<Template>('wf_templates');
      if (filters.status) templates = templates.filter(t => t.status === filters.status);
      if (filters.category) templates = templates.filter(t => t.category === filters.category);
      if (filters.language) templates = templates.filter(t => t.language === filters.language);
      callback(templates);
    };
    handler();
    return subscribe('wf_templates', handler);
  },

  getAll: (): Template[] => getCollection<Template>('wf_templates'),

  getById: (id: string): Template | null =>
    getCollection<Template>('wf_templates').find(t => t.id === id) || null,

  getApproved: (): Template[] =>
    getCollection<Template>('wf_templates').filter(t => t.status === 'approved'),

  getUsable: (): Template[] =>
    getCollection<Template>('wf_templates').filter(t => t.status === 'approved' || t.status === 'draft'),

  create: (data: Partial<Template>): string => {
    const templates = getCollection<Template>('wf_templates');
    const now = new Date().toISOString();
    const template: Template = {
      id: data.id || generateId(),
      name: data.name || '',
      category: data.category || 'MARKETING',
      language: data.language || 'en',
      headerType: data.headerType || 'none',
      headerText: data.headerText,
      headerMediaUrl: data.headerMediaUrl,
      bodyText: data.bodyText || '',
      footerText: data.footerText,
      buttons: data.buttons || [],
      variables: data.variables || [],
      status: data.status || 'draft',
      previewSampleData: data.previewSampleData || {},
      usageCount: data.usageCount || 0,
      metaTemplateId: data.metaTemplateId,
      metaTemplateName: data.metaTemplateName,
      createdAt: data.createdAt || now,
      updatedAt: now,
    };
    templates.push(template);
    setCollection('wf_templates', templates);
    notify('wf_templates');
    _upsertTemplateToDb(template);
    logService.add('templates', 'create', `Template "${template.name}" created`, { templateId: template.id, category: template.category }, 'success', 'template', template.id);
    return template.id;
  },

  update: (id: string, data: Partial<Template>) => {
    const templates = getCollection<Template>('wf_templates');
    const idx = templates.findIndex(t => t.id === id);
    if (idx !== -1) {
      templates[idx] = { ...templates[idx], ...data, updatedAt: new Date().toISOString() };
      setCollection('wf_templates', templates);
      notify('wf_templates');
      _upsertTemplateToDb(templates[idx]);
      logService.add('templates', 'update', `Template "${templates[idx].name}" updated`, { templateId: id }, 'info', 'template', id);
    }
  },

  duplicate: (id: string): string | null => {
    const original = templateService.getById(id);
    if (!original) return null;
    const newId = templateService.create({
      ...original,
      id: undefined as any,
      name: `${original.name} (Copy)`,
      status: 'draft',
      usageCount: 0,
      createdAt: undefined,
    });
    logService.add('templates', 'duplicate', `Template "${original.name}" duplicated`, { sourceId: id, newId }, 'info', 'template', newId);
    return newId;
  },

  archive: (id: string) => {
    const tmpl = templateService.getById(id);
    if (!tmpl) return;
    templateService.update(id, { status: 'archived' });
    logService.add('templates', 'archive', `Template "${tmpl.name}" archived`, { templateId: id }, 'info', 'template', id);
  },

  restore: (id: string) => {
    const tmpl = templateService.getById(id);
    if (!tmpl) return;
    templateService.update(id, { status: 'draft' });
    logService.add('templates', 'restore', `Template "${tmpl.name}" restored to draft`, { templateId: id }, 'info', 'template', id);
  },

  submitToMeta: async (id: string): Promise<{ ok: boolean; error?: string; readableFix?: string }> => {
    const tmpl = templateService.getById(id);
    if (!tmpl) return { ok: false, error: 'Template not found' };

    // Get WhatsApp config
    const integData = _getIntegrationData();
    const wa = integData.whatsapp;
    if (!wa.isConfigured || !wa.businessAccountId || !wa.accessToken) {
      return { ok: false, error: 'WhatsApp not configured', readableFix: 'Configure WhatsApp Cloud API in Integrations first (Business Account ID + Access Token).' };
    }

    logService.add('templates', 'template_create_requested', `Submitting "${tmpl.name}" to Meta`, { templateId: id }, 'info', 'template', id);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      const response = await fetch(`${supabaseUrl}/functions/v1/whatsapp-templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
          'apikey': supabaseKey,
        },
        body: JSON.stringify({
          action: 'create',
          template: tmpl,
          wabaId: wa.businessAccountId,
          accessToken: wa.accessToken,
        }),
      });

      const result = await response.json();

      if (!result.success) {
        const errorMsg = result.error || 'Unknown error';
        logService.add('templates', 'template_create_failed', `Meta rejected "${tmpl.name}": ${errorMsg}`, {
          templateId: id, errorCode: result.errorCode, error: errorMsg, readableFix: result.readableFix,
        }, 'error', 'template', id);
        return { ok: false, error: errorMsg, readableFix: result.readableFix || errorMsg };
      }

      // Success — update local template
      templateService.update(id, {
        status: 'pending',
        metaTemplateId: result.metaTemplateId,
        metaTemplateName: result.metaTemplateName,
        submittedAt: new Date().toISOString(),
      });

      logService.add('templates', 'template_created_in_meta', `Template "${tmpl.name}" submitted to Meta (ID: ${result.metaTemplateId})`, {
        templateId: id, metaTemplateId: result.metaTemplateId, metaTemplateName: result.metaTemplateName, metaStatus: result.status,
      }, 'success', 'template', id);

      return { ok: true };
    } catch (err: any) {
      logService.add('templates', 'template_create_failed', `Network error submitting "${tmpl.name}": ${err.message}`, {
        templateId: id, error: err.message,
      }, 'error', 'template', id);
      return { ok: false, error: err.message, readableFix: 'Network error. Check your connection and try again.' };
    }
  },

  syncFromMeta: async (): Promise<{ ok: boolean; imported: number; updated: number; error?: string }> => {
    const integData = _getIntegrationData();
    const wa = integData.whatsapp;
    if (!wa.isConfigured || !wa.businessAccountId || !wa.accessToken) {
      return { ok: false, imported: 0, updated: 0, error: 'WhatsApp not configured' };
    }

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      const response = await fetch(`${supabaseUrl}/functions/v1/whatsapp-templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
          'apikey': supabaseKey,
        },
        body: JSON.stringify({
          action: 'list',
          wabaId: wa.businessAccountId,
          accessToken: wa.accessToken,
        }),
      });

      const result = await response.json();
      if (!result.success) {
        return { ok: false, imported: 0, updated: 0, error: result.error || 'Failed to fetch templates from Meta' };
      }

      const metaTemplates: any[] = result.templates || [];
      const existingTemplates = getCollection<Template>('wf_templates');
      let imported = 0;
      let updated = 0;

      for (const mt of metaTemplates) {
        // Check if already exists by meta ID or name+language
        const existing = existingTemplates.find(
          t => t.metaTemplateId === mt.id || (t.metaTemplateName === mt.name && t.language === mt.language)
        );

        // Map Meta status
        const statusMap: Record<string, string> = {
          APPROVED: 'approved', REJECTED: 'rejected', PENDING: 'pending',
          PAUSED: 'rejected', DISABLED: 'rejected', IN_APPEAL: 'pending',
        };
        const mappedStatus = statusMap[mt.status] || 'pending';

        // Extract components
        const components = mt.components || [];
        const headerComp = components.find((c: any) => c.type === 'HEADER');
        const bodyComp = components.find((c: any) => c.type === 'BODY');
        const footerComp = components.find((c: any) => c.type === 'FOOTER');
        const buttonsComp = components.find((c: any) => c.type === 'BUTTONS');

        let headerType: string = 'none';
        let headerText: string | undefined;
        let headerMediaUrl: string | undefined;
        if (headerComp) {
          headerType = (headerComp.format || 'text').toLowerCase();
          if (headerType === 'text') headerText = headerComp.text || '';
          // For media headers, try to re-host the image to our storage
          if (['image', 'video', 'document'].includes(headerType) && headerComp.example?.header_handle?.length) {
            const metaMediaUrl = headerComp.example.header_handle[0];
            try {
              // Download image from Meta's CDN and re-upload to our storage
              const mediaResp = await fetch(metaMediaUrl);
              if (mediaResp.ok) {
                const blob = await mediaResp.blob();
                const ext = headerType === 'image' ? 'jpg' : headerType === 'video' ? 'mp4' : 'pdf';
                const path = `headers/meta_${mt.id}_${Date.now()}.${ext}`;
                const { supabase: sb } = await import('@/integrations/supabase/client');
                const { error: uploadErr } = await sb.storage.from('template-media').upload(path, blob, { upsert: true });
                if (!uploadErr) {
                  const { data: urlData } = sb.storage.from('template-media').getPublicUrl(path);
                  headerMediaUrl = urlData.publicUrl;
                } else {
                  console.warn('Failed to re-host template media, using Meta URL as fallback', uploadErr);
                  headerMediaUrl = metaMediaUrl;
                }
              } else {
                console.warn('Failed to download Meta media, using URL as fallback', mediaResp.status);
                headerMediaUrl = metaMediaUrl;
              }
            } catch (reHostErr) {
              console.warn('Re-host error, using Meta URL as fallback', reHostErr);
              headerMediaUrl = metaMediaUrl;
            }
          }
        }

        const buttons: any[] = [];
        if (buttonsComp?.buttons) {
          for (const btn of buttonsComp.buttons) {
            const typeMap: Record<string, string> = {
              QUICK_REPLY: 'quick_reply', URL: 'url', PHONE_NUMBER: 'phone', COPY_CODE: 'copy_code',
            };
            buttons.push({
              type: typeMap[btn.type] || 'quick_reply',
              text: btn.text || '',
              value: btn.url || btn.phone_number || btn.example || '',
            });
          }
        }

        if (existing) {
          // Update status, meta info, and header media
          const updateData: Partial<Template> = {
            status: mappedStatus as any,
            metaTemplateId: mt.id,
            metaTemplateName: mt.name,
            qualityScore: mt.quality_score?.score || existing.qualityScore,
          };
          // Update header media URL if template has media header
          if (['image', 'video', 'document'].includes(headerType) && headerMediaUrl) {
            updateData.headerMediaUrl = headerMediaUrl;
            updateData.headerType = headerType as any;
          }
          templateService.update(existing.id, updateData);
          updated++;
        } else {
          // Import new template
          templateService.create({
            name: mt.name,
            category: mt.category || 'UTILITY',
            language: mt.language || 'en',
            headerType: headerType as any,
            headerText,
            headerMediaUrl,
            bodyText: bodyComp?.text || '',
            footerText: footerComp?.text,
            buttons,
            variables: [],
            status: mappedStatus as any,
            metaTemplateId: mt.id,
            metaTemplateName: mt.name,
          });
          imported++;
        }
      }

      logService.add('templates', 'sync_from_meta', `Synced from Meta: ${imported} imported, ${updated} updated`, {
        totalMeta: metaTemplates.length, imported, updated,
      }, 'success');

      return { ok: true, imported, updated };
    } catch (err: any) {
      return { ok: false, imported: 0, updated: 0, error: err.message };
    }
  },

  // Process template status webhook event
  processTemplateStatusEvent: (payload: any) => {
    const event = payload.event; // e.g. APPROVED, REJECTED, PAUSED, DISABLED, FLAGGED
    const metaId = payload.message_template_id;
    const metaName = payload.message_template_name;
    const reason = payload.reason;

    // Map Meta status events to our statuses
    const statusMap: Record<string, string> = {
      APPROVED: 'approved',
      REJECTED: 'rejected',
      PAUSED: 'rejected', // treat paused as rejected for blocking sends
      DISABLED: 'rejected',
      FLAGGED: 'pending',
      PENDING_DELETION: 'archived',
    };

    const newStatus = statusMap[event] || 'pending';

    // Find local template by metaTemplateId or metaTemplateName
    const allTemplates = getCollection<Template>('wf_templates');
    const tmpl = allTemplates.find(t => t.metaTemplateId === metaId || t.metaTemplateName === metaName);

    if (!tmpl) {
      logService.add('templates', 'template_status_webhook_received', `Template status update for unknown template: ${metaName} (${metaId})`, {
        event, metaId, metaName, reason,
      }, 'warn');
      return;
    }

    const updates: Partial<Template> = {
      status: newStatus as any,
      metaTemplateId: metaId || tmpl.metaTemplateId,
    };

    if (reason) updates.rejectionReason = reason;
    if (event === 'APPROVED') updates.rejectionReason = undefined;

    templateService.update(tmpl.id, updates);

    const level = event === 'APPROVED' ? 'success' : event === 'REJECTED' ? 'error' : 'warn';
    logService.add('templates', 'template_status_updated', `Template "${tmpl.name}" status → ${newStatus} (Meta: ${event})${reason ? ` — Reason: ${reason}` : ''}`, {
      templateId: tmpl.id, metaId, metaName, event, newStatus, reason,
    }, level as any, 'template', tmpl.id);

    if (event === 'REJECTED' && reason) {
      logService.add('templates', 'template_rejected_with_reason', `Template "${tmpl.name}" rejected: ${reason}`, {
        templateId: tmpl.id, reason,
      }, 'error', 'template', tmpl.id);
    }
  },

  approveLocal: (id: string) => {
    // Keep for fallback but mark as local
    const tmpl = templateService.getById(id);
    if (!tmpl) return;
    templateService.update(id, { status: 'approved' });
    logService.add('templates', 'approve', `Template "${tmpl.name}" approved locally (no Meta sync)`, { templateId: id }, 'warn', 'template', id);
  },

  rejectLocal: (id: string) => {
    // Keep for fallback but mark as local
    const tmpl = templateService.getById(id);
    if (!tmpl) return;
    templateService.update(id, { status: 'rejected' });
    logService.add('templates', 'reject', `Template "${tmpl.name}" rejected locally (no Meta sync)`, { templateId: id }, 'warn', 'template', id);
  },

  delete: (id: string): { success: boolean; reason?: string } => {
    // Check if referenced by active campaigns
    const campaigns = getCollection<Campaign>('wf_campaigns');
    const referencedBy = campaigns.filter(c => c.templateId === id && !['cancelled', 'sent'].includes(c.status));
    if (referencedBy.length > 0) {
      templateService.archive(id);
      return { success: false, reason: `Template is referenced by ${referencedBy.length} active campaign(s). Archived instead.` };
    }
    // Check if referenced by automations
    const automations = getCollection<AutomationFlow>('wf_automations');
    const usedInAutomation = automations.filter(a => a.status === 'active' && a.nodes.some(n => n.config?.templateId === id));
    if (usedInAutomation.length > 0) {
      templateService.archive(id);
      return { success: false, reason: `Template is used by ${usedInAutomation.length} active automation(s). Archived instead.` };
    }
    const tmpl = templateService.getById(id);
    setCollection('wf_templates', getCollection<Template>('wf_templates').filter(t => t.id !== id));
    notify('wf_templates');
    void supabase.from('templates' as any).delete().eq('id', id);
    logService.add('templates', 'delete', `Template "${tmpl?.name || id}" deleted`, { templateId: id }, 'info', 'template', id);
    return { success: true };
  },

  validate: (data: Partial<Template>): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];
    if (!data.name?.trim()) errors.push('Template name is required');
    if (!data.bodyText?.trim()) errors.push('Body text is required');
    if (!data.category) errors.push('Category is required');
    if (!data.language) errors.push('Language is required');
    // Check variable placeholders match definitions
    if (data.bodyText && data.variables && data.variables.length > 0) {
      const placeholders = data.bodyText.match(/\{\{(\d+)\}\}/g) || [];
      const placeholderNums = placeholders.map(p => parseInt(p.replace(/\{|\}/g, '')));
      data.variables.forEach((v, i) => {
        if (!placeholderNums.includes(i + 1)) {
          errors.push(`Variable {{${i + 1}}} (${v.label}) not found in body text`);
        }
      });
    }
    // Check buttons
    if (data.buttons) {
      const keys = new Set<string>();
      data.buttons.forEach((btn, i) => {
        if (!btn.text?.trim()) errors.push(`Button ${i + 1} text is required`);
        if (btn.type === 'url' && !btn.value?.trim()) errors.push(`Button ${i + 1} URL is required`);
        if (btn.type === 'phone' && !btn.value?.trim()) errors.push(`Button ${i + 1} phone number is required`);
        if (keys.has(btn.text)) errors.push(`Duplicate button text: "${btn.text}"`);
        keys.add(btn.text);
      });
    }
    // Check duplicate variable keys
    if (data.variables) {
      const varKeys = new Set<string>();
      data.variables.forEach(v => {
        if (varKeys.has(v.key)) errors.push(`Duplicate variable key: "${v.key}"`);
        varKeys.add(v.key);
      });
    }
    return { valid: errors.length === 0, errors };
  },

  getUsageStats: (id: string): { campaignCount: number; automationCount: number; lastUsedAt?: string } => {
    const campaigns = getCollection<Campaign>('wf_campaigns').filter(c => c.templateId === id);
    const automations = getCollection<AutomationFlow>('wf_automations').filter(a => a.nodes.some(n => n.config?.templateId === id));
    let lastUsedAt: string | undefined;
    const sentCampaigns = campaigns.filter(c => c.startedAt).sort((a, b) => new Date(b.startedAt!).getTime() - new Date(a.startedAt!).getTime());
    if (sentCampaigns.length > 0) lastUsedAt = sentCampaigns[0].startedAt;
    return { campaignCount: campaigns.length, automationCount: automations.length, lastUsedAt };
  },
};

// ===================== SETTINGS SERVICE =====================

const _defaultWorkspaceSettings = (): WorkspaceSettings => ({
  id: 'workspace',
  workspaceId: getCurrentWorkspaceId(),
  general: { workspaceName: 'WatiFlow Workspace', workspaceLogo: '', defaultLanguage: '', timezone: '(GMT+00:00) UTC', defaultCountry: '', defaultCurrency: '' },
  preferences: { rtlEnabled: true, notificationsEnabled: true, complianceMode: false },
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

const _getWorkspaceSettings = (): WorkspaceSettings => {
  const stored = getDocument<any>('wf_settings_workspace');
  if (!stored || !stored.general) {
    const def = _defaultWorkspaceSettings();
    setDocument('wf_settings_workspace', def);
    return def;
  }
  const def = _defaultWorkspaceSettings();
  return { ...def, ...stored, general: { ...def.general, ...stored.general }, preferences: { ...def.preferences, ...stored.preferences } };
};

export const settingsService = {
  subscribe: (callback: (settings: WorkspaceSettings) => void) => {
    const handler = () => callback(_getWorkspaceSettings());
    handler();
    return subscribe('wf_settings_workspace', handler);
  },

  subscribeWorkspace: (callback: (settings: WorkspaceSettings) => void) => {
    const handler = () => callback(_getWorkspaceSettings());
    handler();
    return subscribe('wf_settings_workspace', handler);
  },

  get: (): WorkspaceSettings => _getWorkspaceSettings(),

  saveWorkspace: (data: Partial<WorkspaceSettings>): { ok: boolean; errors: string[] } => {
    const current = _getWorkspaceSettings();
    const updated: WorkspaceSettings = {
      ...current,
      general: { ...current.general, ...(data.general || {}) },
      preferences: { ...current.preferences, ...(data.preferences || {}) },
      updatedAt: new Date().toISOString(),
    };
    // Validation
    const errors: string[] = [];
    if (!updated.general.workspaceName.trim()) errors.push('Workspace name is required');
    if (errors.length) return { ok: false, errors };

    setDocument('wf_settings_workspace', updated);
    notify('wf_settings_workspace');

    void supabase.from('workspace_settings' as any).upsert({
      id: getCurrentWorkspaceId(), workspace_id: getCurrentWorkspaceId(),
      general: updated.general, preferences: updated.preferences,
      updated_at: updated.updatedAt,
    } as any, { onConflict: 'id' });

    logService.add('settings', 'settings_updated', 'Workspace settings updated', { changes: Object.keys(data) }, 'success');
    return { ok: true, errors: [] };
  },

  updateWorkspace: (_id: string, data: Partial<WorkspaceSettings>) => {
    settingsService.saveWorkspace(data);
  },

  subscribeTeam: (callback: (members: TeamMember[]) => void) => {
    const handler = () => callback(getCollection<TeamMember>('wf_team'));
    handler();
    return subscribe('wf_team', handler);
  },

  getTeam: (): TeamMember[] => getCollection<TeamMember>('wf_team'),

  addTeamMember: (data: Partial<TeamMember>): { ok: boolean; errors: string[]; id?: string } => {
    const members = getCollection<TeamMember>('wf_team');
    const errors: string[] = [];
    if (!data.name?.trim()) errors.push('Name is required');
    if (!data.email?.trim()) errors.push('Email is required');
    if (data.email && members.some(m => m.email === data.email)) errors.push('A member with this email already exists');
    if (errors.length) return { ok: false, errors };

    const member: TeamMember = {
      id: data.id || generateId(),
      name: data.name || '',
      email: data.email || '',
      role: data.role || 'agent',
      status: data.status || 'invited',
      isActive: data.status === 'active',
      assignedConversationsCount: 0,
      joinedAt: data.joinedAt || new Date().toISOString(),
    };
    members.push(member);
    setCollection('wf_team', members);
    notify('wf_team');
    void supabase.from('team_members' as any).upsert({ id: member.id, name: member.name, email: member.email, role: member.role, status: member.status, joined_at: member.joinedAt, updated_at: new Date().toISOString(), workspace_id: getCurrentWorkspaceId() } as any, { onConflict: 'id' });
    logService.add('settings', 'member_added', `Team member "${member.name}" (${member.role}) added`, { memberId: member.id, role: member.role }, 'success');
    return { ok: true, errors: [], id: member.id };
  },

  updateTeamMember: (id: string, data: Partial<TeamMember>) => {
    const members = getCollection<TeamMember>('wf_team');
    const idx = members.findIndex(m => m.id === id);
    if (idx === -1) return;
    const old = members[idx];
    members[idx] = { ...old, ...data, isActive: (data.status || old.status) === 'active' };
    setCollection('wf_team', members);
    notify('wf_team');
    const m = members[idx];
    void supabase.from('team_members' as any).upsert({ id: m.id, name: m.name, email: m.email, role: m.role, status: m.status, updated_at: new Date().toISOString() } as any, { onConflict: 'id' });
    if (data.role && data.role !== old.role) {
      logService.add('settings', 'role_changed', `${old.name}'s role changed from ${old.role} to ${data.role}`, { memberId: id }, 'info');
    }
    if (data.status && data.status !== old.status) {
      logService.add('settings', 'member_status_changed', `${old.name}'s status changed to ${data.status}`, { memberId: id }, 'info');
    }
  },

  deleteTeamMember: (id: string) => {
    const members = getCollection<TeamMember>('wf_team');
    const member = members.find(m => m.id === id);
    setCollection('wf_team', members.filter(m => m.id !== id));
    notify('wf_team');
    void supabase.from('team_members' as any).delete().eq('id', id);
    logService.add('settings', 'member_removed', `Team member "${member?.name || id}" removed`, { memberId: id }, 'info');
  },

  // Compute assigned conversation count for each member
  refreshTeamAssignments: () => {
    const members = getCollection<TeamMember>('wf_team');
    const conversations = conversationService.getAll();
    let changed = false;
    members.forEach(m => {
      const count = conversations.filter(c => c.assignedAgentId === m.name || c.assignedAgentId === m.id).length;
      if (m.assignedConversationsCount !== count) {
        m.assignedConversationsCount = count;
        changed = true;
      }
    });
    if (changed) {
      setCollection('wf_team', members);
      notify('wf_team');
    }
  },
};

// ===================== AI SERVICE =====================

import {
  generateConversationSummary,
  generateFollowUpSuggestion,
  type AIResponse,
} from './aiMockService';
import { generateReplyReal } from './aiRealService';
import type { AIMemoryEntry } from '../types';

const AI_SETTINGS_STORAGE_KEY = 'wf_ai_settings';

function getAISettingsStorageKey(workspaceId = getCurrentWorkspaceId()) {
  return `${AI_SETTINGS_STORAGE_KEY}:${workspaceId || 'default'}`;
}

function clearLegacyAISettingsStorage() {
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(AI_SETTINGS_STORAGE_KEY);
  }
}

function normalizeAIProvider(provider?: string | null): string {
  const value = String(provider || '').trim();
  if (!value) return 'Lovable AI (Recommended)';

  const lower = value.toLowerCase();
  if (lower === 'lovable' || lower.includes('gemini')) return 'Lovable AI (Recommended)';
  if (lower.includes('openai') || lower.includes('gpt')) return 'OpenAI GPT-5';
  if (lower.includes('claude')) return 'Claude 3.5';
  return value;
}

function normalizeAIModel(model?: string | null): string {
  const value = String(model || '').trim();
  if (!value) return 'google/gemini-3-flash-preview';

  const aliases: Record<string, string> = {
    'gemini-3-flash-preview': 'google/gemini-3-flash-preview',
    'gemini-3.1-pro-preview': 'google/gemini-3.1-pro-preview',
    'gemini-2.5-pro': 'google/gemini-2.5-pro',
    'gemini-2.5-flash': 'google/gemini-2.5-flash',
    'gpt-5': 'openai/gpt-5',
    'gpt-5-mini': 'openai/gpt-5-mini',
  };

  return aliases[value] || value;
}

function splitVoiceConfigFromFeatures(features: unknown) {
  const rawFeatures = features && typeof features === 'object'
    ? { ...(features as Record<string, any>) }
    : {};

  const nestedVoiceConfig =
    rawFeatures.voiceConfig && typeof rawFeatures.voiceConfig === 'object'
      ? { ...(rawFeatures.voiceConfig as Record<string, any>) }
      : {};

  delete rawFeatures.voiceConfig;

  return { cleanFeatures: rawFeatures, nestedVoiceConfig };
}

function normalizeStoredAISettings(stored: Partial<AISettings> | null | undefined): AISettings | null {
  if (!stored) return null;

  const def = _defaultAISettings();
  const { cleanFeatures, nestedVoiceConfig } = splitVoiceConfigFromFeatures((stored as any).features);

  return {
    ...def,
    ...stored,
    provider: normalizeAIProvider(stored.provider),
    model: normalizeAIModel(stored.model),
    apiKey: stored.apiKey || '',
    features: { ...def.features, ...(cleanFeatures as Partial<AISettings['features']>) },
    voiceConfig: {
      ...def.voiceConfig,
      ...(nestedVoiceConfig as Partial<AISettings['voiceConfig']>),
      ...((stored.voiceConfig || {}) as Partial<AISettings['voiceConfig']>),
    },
  };
}

function getStoredAISettings(): AISettings | null {
  return normalizeStoredAISettings(getDocument<AISettings>(getAISettingsStorageKey()));
}

function setStoredAISettings(settings: AISettings) {
  const normalized = normalizeStoredAISettings(settings) || _defaultAISettings();
  setDocument(getAISettingsStorageKey(), normalized);
  clearLegacyAISettingsStorage();
}

const _defaultAISettings = (): AISettings => ({
  id: 'ai_config',
  provider: 'Lovable AI (Recommended)',
  model: 'google/gemini-3-flash-preview',
  apiKey: '',
  apiMode: 'external',
  systemPrompt: 'You are a professional customer support agent for an e-commerce store.',
  brandTone: 'friendly',
  languageRules: 'Respond in the customer\'s language.',
  forbiddenWords: [],
  productContext: '',
  temperature: 0.7,
  maxTokens: 512,
  maxResponseLines: 5,
  confidenceThreshold: 0.85,
  escalationRules: [],
  features: {
    suggestedReplies: true, fullAutoReply: false, intentDetection: true,
    sentimentAnalysis: true, leadQualification: false, orderTracking: true,
    aiMemory: true, smartFollowUp: false, languageDetection: true, imageAnalysis: true, voiceTranscription: true, voiceResponse: false,
  },
  voiceConfig: {
    mode: 'manual' as const,
    voiceId: 'EXAVITQu4vr4xnSDxMaL',
    speed: 1.0,
    stability: 0.50,
    similarityBoost: 0.75,
    style: 0.0,
    dailyLimit: 50,
    fallbackToText: true,
    speechStyle: 'natural' as const,
  },
  updatedAt: new Date().toISOString(),
});

export const aiService = {
  subscribeSettings: (callback: (settings: AISettings) => void) => {
    const handler = () => {
      callback(getStoredAISettings() || _defaultAISettings());
    };

    const wsId = getCurrentWorkspaceId();
    if (wsId) {
      Promise.all([
        supabase
          .from('ai_settings' as any)
          .select('*')
          .eq('workspace_id', wsId)
          .order('updated_at', { ascending: false })
          .limit(1),
        supabase
          .from('integrations_config' as any)
          .select('ai_config')
          .eq('workspace_id', wsId)
          .maybeSingle(),
      ]).then(([settingsResult, integrationsResult]) => {
        const s: any = Array.isArray(settingsResult.data) ? settingsResult.data[0] : settingsResult.data;
        const aiConfig = ((integrationsResult.data as any)?.ai_config || {}) as Record<string, any>;
        if (!s && !aiConfig.apiKey) return;

        const def = _defaultAISettings();
        const { cleanFeatures, nestedVoiceConfig } = splitVoiceConfigFromFeatures((s?.features || {}) as Record<string, any>);
        const aiSettings: AISettings = {
          ...def,
          provider: normalizeAIProvider(s?.provider || def.provider),
          model: normalizeAIModel(s?.model || def.model),
          apiKey: typeof aiConfig.apiKey === 'string' ? aiConfig.apiKey : '',
          apiMode: s?.api_mode || def.apiMode,
          systemPrompt: s?.system_prompt ?? def.systemPrompt,
          brandTone: s?.brand_tone || def.brandTone,
          languageRules: s?.language_rules ?? def.languageRules,
          forbiddenWords: Array.isArray(s?.forbidden_words) ? s.forbidden_words : def.forbiddenWords,
          productContext: s?.product_context ?? def.productContext,
          temperature: s?.temperature ?? def.temperature,
          maxTokens: s?.max_tokens ?? def.maxTokens,
          maxResponseLines: s?.max_response_lines ?? def.maxResponseLines,
          confidenceThreshold: s?.confidence_threshold ?? def.confidenceThreshold,
          escalationRules: Array.isArray(s?.escalation_rules) ? s.escalation_rules : def.escalationRules,
          features: { ...def.features, ...(cleanFeatures as Partial<AISettings['features']>) },
          voiceConfig: { ...def.voiceConfig, ...(nestedVoiceConfig as Partial<AISettings['voiceConfig']>) },
          updatedAt: s?.updated_at || def.updatedAt,
        };
        setStoredAISettings(aiSettings);
        notify('wf_ai_settings');
      });
    }

    handler();
    return subscribe('wf_ai_settings', handler);
  },

  getSettings: (): AISettings => {
    return getStoredAISettings() || _defaultAISettings();
  },

  saveSettings: async (data: Partial<AISettings>): Promise<{ ok: boolean; errors: string[] }> => {
    const current = aiService.getSettings();
    const updated: AISettings = {
      ...current,
      ...data,
      provider: normalizeAIProvider(data.provider ?? current.provider),
      model: normalizeAIModel(data.model ?? current.model),
      apiKey: data.apiKey ?? current.apiKey ?? '',
      features: { ...current.features, ...(data.features || {}) },
      voiceConfig: { ...current.voiceConfig, ...(data.voiceConfig || {}) },
      id: 'ai_config',
      updatedAt: new Date().toISOString(),
    };

    const wsId = getCurrentWorkspaceId();
    const { data: existingIntegrations } = await supabase
      .from('integrations_config' as any)
      .select('ai_config')
      .eq('workspace_id', wsId)
      .maybeSingle();

    const mergedAiConfig = {
      ...((((existingIntegrations as any)?.ai_config) || {}) as Record<string, any>),
      apiKey: updated.apiKey || '',
      isConfigured: Boolean((updated.apiKey || '').trim()),
    };

    const [{ error: settingsError }, { error: integrationsError }] = await Promise.all([
      supabase.from('ai_settings' as any).upsert({
        id: wsId, workspace_id: wsId, provider: updated.provider, model: updated.model,
        api_mode: updated.apiMode, system_prompt: updated.systemPrompt,
        brand_tone: updated.brandTone, language_rules: updated.languageRules,
        forbidden_words: updated.forbiddenWords, product_context: updated.productContext,
        temperature: updated.temperature, max_tokens: updated.maxTokens,
        max_response_lines: updated.maxResponseLines,
        confidence_threshold: updated.confidenceThreshold, escalation_rules: updated.escalationRules,
        features: (() => { const { voiceConfig: _vc, ...cleanFeatures } = (updated.features || {}) as any; return { ...cleanFeatures, voiceConfig: updated.voiceConfig }; })(), updated_at: updated.updatedAt,
      } as any, { onConflict: 'id' }),
      supabase.from('integrations_config' as any).upsert({
        id: wsId,
        workspace_id: wsId,
        ai_config: mergedAiConfig,
        updated_at: updated.updatedAt,
      } as any, { onConflict: 'id' }),
    ]);

    const saveError = settingsError || integrationsError;
    if (saveError) {
      logService.add('ai', 'settings_update_failed', `Failed to save AI configuration: ${saveError.message}`, { error: saveError.message }, 'error');
      return { ok: false, errors: [saveError.message] };
    }

    setStoredAISettings(updated);
    notify('wf_ai_settings');
    logService.add('ai', 'settings_updated', 'AI configuration updated', { provider: updated.provider, model: updated.model }, 'success');
    return { ok: true, errors: [] };
  },

  updateSettings: async (_id: string, data: Partial<AISettings>) => {
    return aiService.saveSettings(data);
  },

  // ---- Test History ----
  subscribeHistory: (callback: (data: AITestEntry[]) => void) => {
    const handler = () => callback(
      getCollection<AITestEntry>('wf_ai_history')
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 50)
    );
    handler();
    return subscribe('wf_ai_history', handler);
  },

  getHistory: (): AITestEntry[] => getCollection<AITestEntry>('wf_ai_history'),

  logHistory: (data: Partial<AITestEntry>): string => {
    const history = getCollection<AITestEntry>('wf_ai_history');
    const entry: AITestEntry = {
      id: generateId(),
      inputMessage: data.inputMessage || '',
      outputMessage: data.outputMessage || '',
      intent: data.intent,
      confidence: data.confidence,
      sentiment: data.sentiment,
      sentimentScore: data.sentimentScore,
      language: data.language,
      leadScore: data.leadScore,
      leadCategory: data.leadCategory,
      orderRelated: data.orderRelated,
      usedSettings: data.usedSettings,
      createdAt: data.createdAt || new Date().toISOString(),
    };
    history.push(entry);
    setCollection('wf_ai_history', history.slice(-100));
    notify('wf_ai_history');
    logService.add('ai', 'ai_test_run', `AI test: "${data.inputMessage?.substring(0, 40)}..."`, { testId: entry.id, intent: data.intent }, 'info');
    return entry.id;
  },

  clearHistory: () => {
    setCollection('wf_ai_history', []);
    notify('wf_ai_history');
  },

  // ---- AI Memory ----
  getMemory: (contactId: string): AIMemoryEntry | null => {
    const memories = getCollection<AIMemoryEntry>('wf_ai_memory');
    return memories.find(m => m.contactId === contactId) || null;
  },

  updateMemory: (contactId: string, data: Partial<AIMemoryEntry>) => {
    const memories = getCollection<AIMemoryEntry>('wf_ai_memory');
    const idx = memories.findIndex(m => m.contactId === contactId);
    const now = new Date().toISOString();
    let entry: AIMemoryEntry;
    if (idx !== -1) {
      memories[idx] = { ...memories[idx], ...data, interactionCount: (memories[idx].interactionCount || 0) + 1, updatedAt: now };
      entry = memories[idx];
    } else {
      entry = {
        id: generateId(),
        contactId,
        summary: data.summary || '',
        lastIntent: data.lastIntent || 'unknown',
        lastSentiment: data.lastSentiment || 'neutral',
        leadScore: data.leadScore || 30,
        interactionCount: 1,
        updatedAt: now,
        createdAt: now,
        ...data,
      };
      memories.push(entry);
    }
    setCollection('wf_ai_memory', memories);
    notify('wf_ai_memory');
    void supabase.from('ai_memory' as any).upsert({
      id: entry.id, contact_id: entry.contactId, conversation_id: entry.conversationId || null,
      summary: entry.summary || '', last_intent: entry.lastIntent || 'unknown', last_sentiment: entry.lastSentiment || 'neutral',
      lead_score: entry.leadScore || 0, lead_category: entry.leadCategory || null,
      interaction_count: entry.interactionCount || 0, preferred_language: entry.preferredLanguage || null,
      last_ai_reply: entry.lastAIReply || null, last_template_used: entry.lastTemplateUsed || null,
      key_facts: entry.keyFacts || [], order_context: entry.orderContext || null,
      last_contact_date: entry.lastContactDate || null, last_message_date: entry.lastMessageDate || null,
      updated_at: entry.updatedAt, created_at: entry.createdAt,
    } as any, { onConflict: 'id' });
  },

  subscribeMemory: (callback: (entries: AIMemoryEntry[]) => void) => {
    const handler = () => callback(getCollection<AIMemoryEntry>('wf_ai_memory'));
    handler();
    return subscribe('wf_ai_memory', handler);
  },

  // ---- Core AI Processing (async - real API with fallback) ----
  processMessageAsync: async (
    message: string,
    contactId?: string,
    conversationId?: string,
    imageUrl?: string,
  ): Promise<AIResponse> => {
    const settings = aiService.getSettings();
    const tone = (settings.brandTone || 'friendly') as string;

    let contactData: any = undefined;
    let orderData: any = undefined;
    if (contactId) {
      const contact = contactService.getById(contactId);
      if (contact) {
        contactData = { fullName: contact.fullName, totalSpent: contact.orderAmount, tags: contact.tags, leadStatus: contact.leadStatus };
        orderData = { orderStatus: contact.orderStatus, deliveryStatus: contact.deliveryStatus, productName: contact.productName, orderAmount: contact.orderAmount };
      }
    }

    let conversationHistory: { direction: string; body: string; replyTo?: { direction: string; body: string } | null }[] = [];
    if (conversationId) {
      const allMsgs = messageService.getByConversation(conversationId);
      // Build wamid -> message lookup so we can resolve "in-reply-to" context
      const byWamid = new Map<string, { direction: string; body: string }>();
      allMsgs.forEach(m => {
        if (m.externalMessageId) byWamid.set(m.externalMessageId, { direction: m.direction, body: m.body });
      });
      const last = allMsgs.slice(-10);
      conversationHistory = last.map(m => {
        const replyTo = m.contextWamid ? byWamid.get(m.contextWamid) : undefined;
        return {
          direction: m.direction,
          body: m.body,
          replyTo: replyTo ? { direction: replyTo.direction, body: replyTo.body } : null,
        };
      });
    }

    let aiMemory: string | undefined;
    if (contactId) {
      const mem = aiService.getMemory(contactId);
      if (mem) aiMemory = mem.summary;
    }

    const result = await generateReplyReal(message, settings, {
      contactData, orderData, conversationHistory, tone, aiMemory,
      contactId, conversationId, imageUrl,
    });

    aiService._applyPostProcessing(result, settings, contactId, conversationId);
    return result;
  },

  // ---- Shared post-processing ----
  _applyPostProcessing: (result: AIResponse, settings: AISettings, contactId?: string, conversationId?: string) => {
    // Update AI memory if feature is enabled
    if (settings.features.aiMemory && contactId) {
      const contact = contactService.getById(contactId);
      const existingMemory = aiService.getMemory(contactId);

      // Build conversation summary from recent messages
      let conversationSummary = existingMemory?.summary || '';
      if (conversationId) {
        const msgs = messageService.getByConversation(conversationId);
        const recent = msgs.slice(-6);
        if (recent.length > 0) {
          const lastInbound = recent.filter(m => m.direction === 'inbound').slice(-1)[0];
          const summaryParts: string[] = [];
          if (lastInbound) summaryParts.push(`Last customer message: "${lastInbound.body.substring(0, 100)}"`);
          if (result.intent && result.intent !== 'unknown') summaryParts.push(`Intent: ${result.intent}`);
          if (result.sentiment) summaryParts.push(`Mood: ${result.sentiment}`);
          if (result.orderRelated) summaryParts.push('Order-related inquiry');
          conversationSummary = summaryParts.join('. ');
        }
      }

      // Build order context
      let orderContext: string | undefined;
      if (contact) {
        const orderParts: string[] = [];
        if (contact.orderStatus) orderParts.push(`Order: ${contact.orderStatus}`);
        if (contact.productName) orderParts.push(`Product: ${contact.productName}`);
        if (contact.deliveryStatus) orderParts.push(`Delivery: ${contact.deliveryStatus}`);
        if (orderParts.length) orderContext = orderParts.join(', ');
      }

      // Build key facts from existing + new
      const keyFacts = [...(existingMemory?.keyFacts || [])];
      if (result.orderRelated && !keyFacts.includes('Has order inquiries')) keyFacts.push('Has order inquiries');
      if (result.shouldEscalate && !keyFacts.includes('Required escalation before')) keyFacts.push('Required escalation before');
      if (result.sentiment === 'negative' && !keyFacts.includes('Has expressed dissatisfaction')) keyFacts.push('Has expressed dissatisfaction');
      // Keep max 10 facts
      const trimmedFacts = keyFacts.slice(-10);

      aiService.updateMemory(contactId, {
        lastIntent: result.intent,
        lastSentiment: result.sentiment,
        leadScore: result.leadScore,
        leadCategory: result.leadCategory,
        conversationId,
        summary: conversationSummary,
        preferredLanguage: result.language || existingMemory?.preferredLanguage,
        lastAIReply: result.reply?.substring(0, 200),
        keyFacts: trimmedFacts,
        orderContext,
        lastContactDate: new Date().toISOString(),
        lastMessageDate: new Date().toISOString(),
      });
    }

    // Update contact lead score if lead qualification enabled
    if (settings.features.leadQualification && contactId) {
      const contact = contactService.getById(contactId);
      if (contact) {
        const newStatus = result.leadCategory === 'hot' ? 'qualified' : result.leadCategory === 'warm' ? 'lead' : 'new';
        contactService.update(contactId, {
          leadStatus: contact.leadStatus === 'customer' ? 'customer' : newStatus as any,
        });
        logService.add('ai', 'lead_scored', `Lead score updated for ${contact.fullName}: ${result.leadScore} (${result.leadCategory})`, { contactId, score: result.leadScore }, 'info');
      }
    }

    if ((result as any)._fallback) {
      logService.add('ai', 'ai_fallback_used', `AI fell back to local mock: ${(result as any)._error || 'unknown reason'}`, {
        contactId, conversationId, error: (result as any)._error,
      }, 'warn');
    }

    logService.add('ai', 'ai_response_generated', `AI processed message: intent=${result.intent}, sentiment=${result.sentiment}${(result as any)._fallback ? ' (fallback)' : ''}`, {
      contactId, conversationId, intent: result.intent, confidence: result.confidence, fallback: (result as any)._fallback || false,
    }, 'info');
  },

  // ---- Auto Reply (async - real API with fallback) ----
  handleAutoReplyAsync: async (messageBody: string, contactId: string, conversationId: string): Promise<boolean> => {
    const settings = aiService.getSettings();
    if (!settings.features.fullAutoReply) return false;
    const contact = contactService.getById(contactId);
    if (!contact) return false;
    if (blacklistService.isBlacklisted(contact.phoneNumber)) return false;

    // 24h window check — don't send free-form AI reply outside the window
    try {
      const { is24hWindowOpen, isWhatsAppConfigured } = await import('./whatsappService');
      if (isWhatsAppConfigured()) {
        const windowState = is24hWindowOpen(conversationId);
        if (!windowState.open) {
          logService.add('ai', 'auto_reply_blocked_24h', `AI auto-reply blocked: 24h window expired for contact ${contactId}. Template required.`, { contactId, conversationId }, 'warn');
          return false;
        }
      }
    } catch { /* if import fails, proceed */ }

    const result = await aiService.processMessageAsync(messageBody, contactId, conversationId);
    if (result.shouldEscalate) {
      logService.add('ai', 'auto_reply_skipped', `Auto-reply skipped (escalation needed): ${result.escalationReason}`, { contactId, conversationId }, 'warn');
      return false;
    }

    const replyMsg = messageService.create({
      conversationId, contactId,
      direction: 'outbound', messageType: 'text',
      body: result.reply, deliveryState: 'delivered',
      sentBy: 'ai_auto_reply',
    });
    logService.add('ai', 'ai_auto_reply_sent', `AI auto-replied (intent: ${result.intent})${(result as any)._fallback ? ' (fallback)' : ''}`, { contactId, conversationId, intent: result.intent, messageId: replyMsg }, 'success');
    return true;
  },

  // ---- Follow-up Suggestions ----
  getFollowUpSuggestion: (contactId: string): string | null => {
    const settings = aiService.getSettings();
    if (!settings.features.smartFollowUp) return null;

    const memory = aiService.getMemory(contactId);
    if (!memory) return null;

    const hoursSince = (Date.now() - new Date(memory.updatedAt).getTime()) / (1000 * 60 * 60);
    return generateFollowUpSuggestion(memory.lastIntent, hoursSince);
  },

  // ---- Conversation Summary ----
  generateSummary: (conversationId: string): string => {
    const msgs = messageService.getByConversation(conversationId);
    return generateConversationSummary(msgs.map(m => ({ direction: m.direction, body: m.body })));
  },
};

// ===================== INTEGRATION SERVICE =====================

export interface WhatsAppConfig {
  phoneNumberId: string;
  businessAccountId: string;
  accessToken: string;
  isConfigured: boolean;
  lastValidatedAt: string | null;
}

export interface AIConfig {
  provider: string;
  apiKey: string;
  model: string;
  isConfigured: boolean;
  lastValidatedAt: string | null;
}

export interface WebhookConfig {
  callbackUrl: string;
  verifyToken: string;
  isVerified: boolean;
  lastTestAt: string | null;
}

export interface EcommerceConfig {
  connectionString: string;
  platform: string;
  isConfigured: boolean;
  lastValidatedAt: string | null;
}

export type IntegrationStatus = 'not_configured' | 'pending' | 'configured' | 'verified';

export interface IntegrationData {
  id: string;
  workspaceId: string;
  whatsapp: WhatsAppConfig;
  ai: AIConfig;
  webhook: WebhookConfig;
  ecommerce: EcommerceConfig;
  whatsappStatus: IntegrationStatus;
  aiStatus: IntegrationStatus;
  webhookStatus: IntegrationStatus;
  ecommerceStatus: IntegrationStatus;
  createdAt: string;
  updatedAt: string;
}

const getPublicWebhookUrl = (): string => {
  const cloudBaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (cloudBaseUrl) {
    return `${cloudBaseUrl}/functions/v1/whatsapp-webhook/webhook`;
  }
  return `${typeof window !== 'undefined' ? window.location.origin : ''}/webhook`;
};

const defaultIntegrationData = (): IntegrationData => ({
  id: 'integration-main',
  workspaceId: getCurrentWorkspaceId(),
  whatsapp: { phoneNumberId: '', businessAccountId: '', accessToken: '', isConfigured: false, lastValidatedAt: null },
  ai: { provider: 'Google Gemini', apiKey: '', model: 'gemini-3-flash-preview', isConfigured: false, lastValidatedAt: null },
  webhook: { callbackUrl: getPublicWebhookUrl(), verifyToken: 'watidash_verify_2026', isVerified: false, lastTestAt: null },
  ecommerce: { connectionString: '', platform: 'custom', isConfigured: false, lastValidatedAt: null },
  whatsappStatus: 'not_configured',
  aiStatus: 'not_configured',
  webhookStatus: 'not_configured',
  ecommerceStatus: 'not_configured',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

let _dbHydrated = false;
let _dbHydrating = false;

/** Reset hydration flag so next subscribe/get triggers a fresh DB load (e.g. after login) */
export function resetIntegrationHydration() {
  _dbHydrated = false;
  _dbHydrating = false;
}

/** Force re-hydrate from DB — called after login to ensure DB is source of truth */
export async function forceHydrateIntegrationsFromDb() {
  _dbHydrated = false;
  _dbHydrating = false;
  await _hydrateFromDb();
}

const _getIntegrationData = (): IntegrationData => {
  const wsId = getCurrentWorkspaceId();
  const stored = getDocument<any>(`wf_integrations:${wsId}`);
  if (!stored || !stored.whatsapp) {
    const def = defaultIntegrationData();
    setDocument(`wf_integrations:${wsId}`, def);
    return def;
  }
  const def = defaultIntegrationData();
  const merged: IntegrationData = {
    ...def,
    ...stored,
    whatsapp: { ...def.whatsapp, ...stored.whatsapp },
    ai: { ...def.ai, ...stored.ai },
    webhook: { ...def.webhook, ...stored.webhook },
    ecommerce: { ...def.ecommerce, ...stored.ecommerce },
  };

  const legacyLocalWebhookUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/webhook`;
  if (!merged.webhook.callbackUrl || merged.webhook.callbackUrl === legacyLocalWebhookUrl) {
    merged.webhook.callbackUrl = getPublicWebhookUrl();
  }

  return merged;
};

const _hydrateFromDb = async () => {
  if (_dbHydrated || _dbHydrating) return;
  _dbHydrating = true;
  try {
    const { data: row } = await supabase.from('integrations_config').select('*').eq('workspace_id', getCurrentWorkspaceId()).maybeSingle();
    if (row) {
      const def = defaultIntegrationData();
      const wa = row.whatsapp_config ? { ...def.whatsapp, ...(row.whatsapp_config as any) } : def.whatsapp;
      const ai = row.ai_config ? { ...def.ai, ...(row.ai_config as any) } : def.ai;
      const whk = row.webhook_config ? { ...def.webhook, ...(row.webhook_config as any) } : def.webhook;
      const ecom = row.ecommerce_db_config ? { ...def.ecommerce, ...(row.ecommerce_db_config as any) } : def.ecommerce;
      
      // Always use the real webhook URL
      whk.callbackUrl = getPublicWebhookUrl();

      const merged: IntegrationData = {
        ...def,
        whatsapp: wa,
        ai: ai,
        webhook: whk,
        ecommerce: ecom,
        whatsappStatus: _computeWhatsappStatus(wa),
        aiStatus: _computeAiStatus(ai),
        webhookStatus: _computeWebhookStatus(whk),
        ecommerceStatus: _computeEcommerceStatus(ecom),
        updatedAt: row.updated_at || def.updatedAt,
      };
      setDocument(`wf_integrations:${getCurrentWorkspaceId()}`, merged);
      notify('wf_integrations');
    }
  } catch (err) {
    console.warn('Failed to hydrate integrations from DB:', err);
  } finally {
    _dbHydrated = true;
    _dbHydrating = false;
  }
};

const _computeWhatsappStatus = (c: WhatsAppConfig): IntegrationStatus => {
  if (!c.phoneNumberId && !c.businessAccountId && !c.accessToken) return 'not_configured';
  if (c.phoneNumberId && c.businessAccountId && c.accessToken) return 'configured';
  return 'pending';
};

const _computeAiStatus = (c: AIConfig): IntegrationStatus => {
  if (!c.apiKey) return 'not_configured';
  return 'configured';
};

const _mapWhatsAppErrorToFix = (code: number | string, message: string): string => {
  const codeNum = typeof code === 'string' ? parseInt(code, 10) : code;
  const map: Record<number, string> = {
    131005: 'Access denied. Your token may lack permissions. Generate a permanent System User token with whatsapp_business_messaging permission.',
    131047: '24-hour customer service window has expired. You can only send approved template messages outside this window.',
    133010: 'Phone number is not registered. Verify the phone number is properly linked in Meta Business Manager.',
    131031: 'Business account is locked or restricted. Check Meta Business Manager for compliance issues.',
    131026: 'Message delivery failed. The recipient may have blocked you or the number is invalid.',
    131051: 'Unsupported message type. Check the message format.',
    131009: 'Parameter is invalid or missing. Check your API call parameters.',
    190: 'Access token expired or invalid. Generate a new permanent token from Meta Business Settings → System Users.',
    100: 'Invalid parameter. Check Phone Number ID and Business Account ID are correct.',
    10: 'Permission denied. Ensure your app has whatsapp_business_messaging permission.',
    4: 'Rate limit reached. Wait a few minutes and try again.',
  };
  return map[codeNum] || `Error ${code}: ${message}. Check Meta Business Manager for details.`;
};

const _computeWebhookStatus = (c: WebhookConfig): IntegrationStatus => {
  if (c.isVerified) return 'verified';
  if (c.verifyToken) return 'configured';
  return 'not_configured';
};

const _computeEcommerceStatus = (c: EcommerceConfig): IntegrationStatus => {
  if (!c.connectionString) return 'not_configured';
  return 'configured';
};

export const integrationService = {
  subscribe: (callback: (data: IntegrationData) => void) => {
    const handler = () => callback(_getIntegrationData());
    handler();
    // Hydrate from DB on first subscribe (will notify if data found)
    _hydrateFromDb();
    return subscribe('wf_integrations', handler);
  },

  get: (): IntegrationData => _getIntegrationData(),

  saveWhatsApp: (config: Partial<WhatsAppConfig>): { ok: boolean; errors: string[] } => {
    const errors: string[] = [];
    if (!config.phoneNumberId?.trim()) errors.push('Phone Number ID is required');
    if (!config.businessAccountId?.trim()) errors.push('Business Account ID is required');
    if (!config.accessToken?.trim()) errors.push('Access Token is required');
    if (errors.length) return { ok: false, errors };

    const data = _getIntegrationData();
    data.whatsapp = { ...data.whatsapp, ...config, isConfigured: true };
    data.whatsappStatus = _computeWhatsappStatus(data.whatsapp);
    data.updatedAt = new Date().toISOString();
    setDocument(`wf_integrations:${getCurrentWorkspaceId()}`, data);

    void supabase.from('integrations_config' as any).upsert({
      id: getCurrentWorkspaceId(),
      workspace_id: getCurrentWorkspaceId(),
      whatsapp_config: data.whatsapp,
      updated_at: data.updatedAt,
    } as any, { onConflict: 'id' }).then(({ error }) => {
      if (error) {
        logService.add('integrations', 'whatsapp_config_cloud_sync_failed', `Failed to sync WhatsApp config to backend: ${error.message}`, {
          section: 'whatsapp',
          error: error.message,
        }, 'warn');
        return;
      }

      logService.add('integrations', 'whatsapp_config_cloud_synced', 'WhatsApp Cloud API configuration synced to backend', {
        section: 'whatsapp',
      }, 'info');
    });

    notify('wf_integrations');
    logService.add('integrations', 'whatsapp_config_saved', 'WhatsApp Cloud API configuration saved', { section: 'whatsapp' }, 'success');
    return { ok: true, errors: [] };
  },

  testWhatsApp: async (): Promise<{
    ok: boolean; message: string;
    diagnostic?: {
      status: 'success' | 'failed';
      steps: { config: 'ok' | 'error' | 'skipped'; token: 'ok' | 'error' | 'skipped'; send: 'ok' | 'error' | 'skipped' };
      error?: { code: number | string; message: string; readableFix: string };
      phoneDisplay?: string;
      latencyMs?: number;
    };
  }> => {
    const data = _getIntegrationData();
    const startTime = Date.now();

    // Step 1: Validate config
    const missingFields: string[] = [];
    if (!data.whatsapp.accessToken) missingFields.push('Access Token');
    if (!data.whatsapp.phoneNumberId) missingFields.push('Phone Number ID');
    if (!data.whatsapp.businessAccountId) missingFields.push('Business Account ID');

    if (missingFields.length > 0 || !data.whatsapp.isConfigured) {
      const diag = {
        status: 'failed' as const,
        steps: { config: 'error' as const, token: 'skipped' as const, send: 'skipped' as const },
        error: { code: 'CONFIG_MISSING', message: `Missing: ${missingFields.join(', ')}`, readableFix: 'Fill in all required fields and save before testing.' },
      };
      logService.add('integrations', 'whatsapp_tested', `WhatsApp test failed: missing config (${missingFields.join(', ')})`, { diagnostic: diag }, 'error');
      return { ok: false, message: `Missing: ${missingFields.join(', ')}`, diagnostic: diag };
    }

    // Step 2: Validate token via GET /me
    try {
      const meRes = await fetch('https://graph.facebook.com/v22.0/me', {
        headers: { 'Authorization': `Bearer ${data.whatsapp.accessToken}` },
      });
      const meData = await meRes.json();

      if (!meRes.ok) {
        const errMsg = meData?.error?.message || `HTTP ${meRes.status}`;
        const errCode = meData?.error?.code || meRes.status;
        const diag = {
          status: 'failed' as const,
          steps: { config: 'ok' as const, token: 'error' as const, send: 'skipped' as const },
          error: { code: errCode, message: errMsg, readableFix: errCode === 190 ? 'Your access token is invalid or expired. Generate a new permanent token from Meta Business Settings → System Users.' : 'Check your access token in Meta Developer Dashboard.' },
          latencyMs: Date.now() - startTime,
        };
        logService.add('integrations', 'whatsapp_tested', `WhatsApp token validation failed: ${errMsg}`, { diagnostic: diag }, 'error');
        return { ok: false, message: `Invalid token: ${errMsg}`, diagnostic: diag };
      }

      // Step 3: Validate phone number ID
      const phoneRes = await fetch(`https://graph.facebook.com/v22.0/${data.whatsapp.phoneNumberId}`, {
        headers: { 'Authorization': `Bearer ${data.whatsapp.accessToken}` },
      });
      const phoneData = await phoneRes.json();

      data.whatsapp.lastValidatedAt = new Date().toISOString();
      data.updatedAt = new Date().toISOString();
      setDocument(`wf_integrations:${getCurrentWorkspaceId()}`, data);
      notify('wf_integrations');

      if (!phoneRes.ok) {
        const errMsg = phoneData?.error?.message || `HTTP ${phoneRes.status}`;
        const errCode = phoneData?.error?.code || phoneRes.status;
        const readableFix = _mapWhatsAppErrorToFix(errCode, errMsg);
        const diag = {
          status: 'failed' as const,
          steps: { config: 'ok' as const, token: 'ok' as const, send: 'error' as const },
          error: { code: errCode, message: errMsg, readableFix },
          latencyMs: Date.now() - startTime,
        };
        logService.add('integrations', 'whatsapp_tested', `WhatsApp phone validation failed: ${errMsg}`, { diagnostic: diag }, 'error');
        return { ok: false, message: `Phone Number ID error: ${errMsg}`, diagnostic: diag };
      }

      const phoneDisplay = phoneData.display_phone_number || phoneData.verified_name || data.whatsapp.phoneNumberId;
      const diag = {
        status: 'success' as const,
        steps: { config: 'ok' as const, token: 'ok' as const, send: 'ok' as const },
        phoneDisplay,
        latencyMs: Date.now() - startTime,
      };
      logService.add('integrations', 'whatsapp_tested', `WhatsApp API fully verified (Phone: ${phoneDisplay})`, { diagnostic: diag }, 'success');
      return { ok: true, message: `WhatsApp connected! Phone: ${phoneDisplay}`, diagnostic: diag };

    } catch (err: any) {
      const diag = {
        status: 'failed' as const,
        steps: { config: 'ok' as const, token: 'error' as const, send: 'skipped' as const },
        error: { code: 'NETWORK_ERROR', message: err.message, readableFix: 'Network error. Check your internet connection or try again later.' },
        latencyMs: Date.now() - startTime,
      };
      logService.add('integrations', 'whatsapp_tested', `WhatsApp test network error: ${err.message}`, { diagnostic: diag }, 'error');
      return { ok: false, message: `Network error: ${err.message}`, diagnostic: diag };
    }
  },

  saveAI: (config: Partial<AIConfig>): { ok: boolean; errors: string[] } => {
    const errors: string[] = [];
    if (!config.apiKey?.trim()) errors.push('API Key is required');
    if (!config.provider?.trim()) errors.push('Provider is required');
    if (errors.length) return { ok: false, errors };

    const data = _getIntegrationData();
    data.ai = { ...data.ai, ...config, isConfigured: true };
    data.aiStatus = _computeAiStatus(data.ai);
    data.updatedAt = new Date().toISOString();
    setDocument(`wf_integrations:${getCurrentWorkspaceId()}`, data);
    notify('wf_integrations');

    void supabase.from('integrations_config' as any).upsert({
      id: getCurrentWorkspaceId(),
      workspace_id: getCurrentWorkspaceId(),
      ai_config: data.ai,
      updated_at: data.updatedAt,
    } as any, { onConflict: 'id' });

    logService.add('integrations', 'ai_config_saved', `AI engine configuration saved (${config.provider})`, { section: 'ai', provider: config.provider }, 'success');
    return { ok: true, errors: [] };
  },

  testAI: (): { ok: boolean; message: string; response?: string } => {
    const data = _getIntegrationData();
    if (!data.ai.isConfigured) return { ok: false, message: 'AI engine is not configured. Save config first.' };
    data.ai.lastValidatedAt = new Date().toISOString();
    data.updatedAt = new Date().toISOString();
    setDocument(`wf_integrations:${getCurrentWorkspaceId()}`, data);
    notify('wf_integrations');
    const mockResponse = `{"intent":"greeting","confidence":0.95,"reply":"Hello! How can I help you today?","model":"${data.ai.model}"}`;
    logService.add('integrations', 'ai_tested', `AI engine test completed (${data.ai.provider} / ${data.ai.model})`, { result: 'success_simulated', mockResponse }, 'success');
    return { ok: true, message: 'AI test completed (local simulation).', response: mockResponse };
  },

  saveWebhook: (config: Partial<WebhookConfig>): { ok: boolean; errors: string[] } => {
    const errors: string[] = [];
    if (!config.verifyToken?.trim()) errors.push('Verify Token is required');
    if (errors.length) return { ok: false, errors };

    const data = _getIntegrationData();
    data.webhook = { ...data.webhook, ...config, callbackUrl: getPublicWebhookUrl() };
    data.webhookStatus = _computeWebhookStatus(data.webhook);
    data.updatedAt = new Date().toISOString();
    setDocument(`wf_integrations:${getCurrentWorkspaceId()}`, data);
    notify('wf_integrations');

    void supabase.from('integrations_config' as any).upsert({
      id: getCurrentWorkspaceId(),
      workspace_id: getCurrentWorkspaceId(),
      webhook_config: data.webhook,
      updated_at: data.updatedAt,
    } as any, { onConflict: 'id' });

    logService.add('integrations', 'webhook_config_saved', 'Webhook configuration saved', {
      section: 'webhook',
      callbackUrl: data.webhook.callbackUrl,
    }, 'success');
    return { ok: true, errors: [] };
  },

  testWebhook: async (): Promise<{
    ok: boolean; message: string;
    diagnostic?: {
      status: 'success' | 'failed';
      steps: { reachable: 'ok' | 'error' | 'skipped'; tokenMatch: 'ok' | 'error' | 'skipped'; challengeReturn: 'ok' | 'error' | 'skipped' };
      error?: { code: string; message: string; readableFix: string };
      latencyMs?: number;
    };
  }> => {
    const data = _getIntegrationData();
    if (!data.webhook.verifyToken) {
      return {
        ok: false, message: 'Webhook verify token is not set.',
        diagnostic: { status: 'failed', steps: { reachable: 'skipped', tokenMatch: 'skipped', challengeReturn: 'skipped' }, error: { code: 'NO_TOKEN', message: 'Verify token not configured', readableFix: 'Enter a verify token and save before testing.' } },
      };
    }

    const callbackUrl = data.webhook.callbackUrl || getPublicWebhookUrl();
    const challenge = String(Math.floor(Math.random() * 999999999));
    const testUrl = `${callbackUrl}?hub.mode=subscribe&hub.verify_token=${encodeURIComponent(data.webhook.verifyToken)}&hub.challenge=${challenge}`;
    const startTime = Date.now();

    try {
      const response = await fetch(testUrl, { method: 'GET' });
      const body = await response.text();
      const latencyMs = Date.now() - startTime;

      data.webhook.lastTestAt = new Date().toISOString();

      if (response.status !== 200) {
        data.webhook.isVerified = false;
        data.webhookStatus = _computeWebhookStatus(data.webhook);
        data.updatedAt = new Date().toISOString();
        setDocument(`wf_integrations:${getCurrentWorkspaceId()}`, data);
        notify('wf_integrations');

        const diag = {
          status: 'failed' as const, latencyMs,
          steps: { reachable: 'ok' as const, tokenMatch: 'error' as const, challengeReturn: 'skipped' as const },
          error: { code: `HTTP_${response.status}`, message: `Server returned HTTP ${response.status}: ${body.substring(0, 200)}`, readableFix: response.status === 403 ? 'Verify token does not match. Make sure the token in Meta Dashboard matches what you entered here.' : response.status === 404 ? 'Webhook endpoint not found. Check that the edge function is deployed correctly.' : `Unexpected status ${response.status}. Check your webhook server logs.` },
        };
        logService.add('integrations', 'webhook_verification_failed', `Webhook test failed: HTTP ${response.status}`, { diagnostic: diag }, 'error');
        return { ok: false, message: `Webhook returned HTTP ${response.status}`, diagnostic: diag };
      }

      // Check challenge match
      if (body.trim() !== challenge) {
        data.webhook.isVerified = false;
        data.webhookStatus = _computeWebhookStatus(data.webhook);
        data.updatedAt = new Date().toISOString();
        setDocument(`wf_integrations:${getCurrentWorkspaceId()}`, data);
        notify('wf_integrations');

        const diag = {
          status: 'failed' as const, latencyMs,
          steps: { reachable: 'ok' as const, tokenMatch: 'ok' as const, challengeReturn: 'error' as const },
          error: { code: 'CHALLENGE_MISMATCH', message: `Expected "${challenge}", got "${body.trim().substring(0, 50)}"`, readableFix: 'The webhook returned 200 but did not echo the challenge. Check your webhook handler returns hub.challenge as plain text.' },
        };
        logService.add('integrations', 'webhook_verification_failed', 'Webhook challenge mismatch', { diagnostic: diag }, 'error');
        return { ok: false, message: 'Challenge mismatch — webhook did not echo correctly', diagnostic: diag };
      }

      // Success
      data.webhook.isVerified = true;
      data.webhookStatus = 'verified';
      data.updatedAt = new Date().toISOString();
      setDocument(`wf_integrations:${getCurrentWorkspaceId()}`, data);
      notify('wf_integrations');

      const diag = {
        status: 'success' as const, latencyMs,
        steps: { reachable: 'ok' as const, tokenMatch: 'ok' as const, challengeReturn: 'ok' as const },
      };
      logService.add('integrations', 'webhook_verified_success', `Webhook verified (${latencyMs}ms)`, { diagnostic: diag }, 'success');
      return { ok: true, message: `Webhook verified (${latencyMs}ms)`, diagnostic: diag };

    } catch (error: any) {
      const latencyMs = Date.now() - startTime;
      data.webhook.isVerified = false;
      data.webhookStatus = _computeWebhookStatus(data.webhook);
      data.webhook.lastTestAt = new Date().toISOString();
      data.updatedAt = new Date().toISOString();
      setDocument(`wf_integrations:${getCurrentWorkspaceId()}`, data);
      notify('wf_integrations');

      const diag = {
        status: 'failed' as const, latencyMs,
        steps: { reachable: 'error' as const, tokenMatch: 'skipped' as const, challengeReturn: 'skipped' as const },
        error: { code: 'UNREACHABLE', message: error?.message || 'Connection failed', readableFix: 'Cannot reach webhook URL. Check that the edge function is deployed and the URL is correct.' },
      };
      logService.add('integrations', 'webhook_error', `Webhook unreachable: ${error?.message}`, { diagnostic: diag }, 'error');
      return { ok: false, message: `Webhook unreachable: ${error?.message || 'unknown error'}`, diagnostic: diag };
    }
  },

  saveEcommerce: (config: Partial<EcommerceConfig>): { ok: boolean; errors: string[] } => {
    const errors: string[] = [];
    if (!config.connectionString?.trim()) errors.push('Connection string is required');
    if (errors.length) return { ok: false, errors };

    const data = _getIntegrationData();
    data.ecommerce = { ...data.ecommerce, ...config, isConfigured: true };
    data.ecommerceStatus = _computeEcommerceStatus(data.ecommerce);
    data.updatedAt = new Date().toISOString();
    setDocument(`wf_integrations:${getCurrentWorkspaceId()}`, data);
    notify('wf_integrations');

    void supabase.from('integrations_config' as any).upsert({
      id: getCurrentWorkspaceId(),
      workspace_id: getCurrentWorkspaceId(),
      ecommerce_db_config: data.ecommerce,
      updated_at: data.updatedAt,
    } as any, { onConflict: 'id' });

    logService.add('integrations', 'ecommerce_config_saved', `E-commerce connection saved (${config.platform || 'custom'})`, { section: 'ecommerce' }, 'success');
    return { ok: true, errors: [] };
  },

  testEcommerce: (): { ok: boolean; message: string } => {
    const data = _getIntegrationData();
    if (!data.ecommerce.isConfigured) return { ok: false, message: 'E-commerce is not configured.' };
    data.ecommerce.lastValidatedAt = new Date().toISOString();
    data.updatedAt = new Date().toISOString();
    setDocument(`wf_integrations:${getCurrentWorkspaceId()}`, data);
    notify('wf_integrations');
    logService.add('integrations', 'ecommerce_tested', 'E-commerce connection test simulated', { result: 'success_simulated' }, 'success');
    return { ok: true, message: 'E-commerce connection test passed (local simulation).' };
  },

  // ─── ElevenLabs per-workspace ───
  saveElevenLabs: async (config: { apiKey: string; voiceId: string }): Promise<{ ok: boolean; errors: string[] }> => {
    const wsId = getCurrentWorkspaceId();
    const { data: existing } = await supabase.from('integrations_config').select('ai_config').eq('workspace_id', wsId).maybeSingle();
    const mergedAiConfig = { ...((existing as any)?.ai_config || {}), elevenlabs: { apiKey: config.apiKey, voiceId: config.voiceId, isConfigured: Boolean(config.apiKey.trim()) } };
    await supabase.from('integrations_config' as any).upsert({
      id: wsId, workspace_id: wsId, ai_config: mergedAiConfig, updated_at: new Date().toISOString(),
    } as any, { onConflict: 'id' });
    // Cache per-workspace in localStorage
    localStorage.setItem(`elevenlabs_config_${wsId}`, JSON.stringify(config));
    localStorage.removeItem('elevenlabs_config'); // clean old global key
    logService.add('integrations', 'elevenlabs_saved', 'ElevenLabs config saved', { voiceId: config.voiceId }, 'success');
    return { ok: true, errors: [] };
  },

  loadElevenLabs: async (): Promise<{ apiKey: string; voiceId: string; isConfigured: boolean }> => {
    const wsId = getCurrentWorkspaceId();
    const { data: row } = await supabase.from('integrations_config').select('ai_config').eq('workspace_id', wsId).maybeSingle();
    const el = (row as any)?.ai_config?.elevenlabs;
    if (el?.apiKey) {
      localStorage.setItem(`elevenlabs_config_${wsId}`, JSON.stringify({ apiKey: el.apiKey, voiceId: el.voiceId }));
      return { apiKey: el.apiKey, voiceId: el.voiceId || 'EXAVITQu4vr4xnSDxMaL', isConfigured: true };
    }
    // No cross-workspace fallback — each workspace must have its own config
    // Clean up old global localStorage key to prevent leakage
    localStorage.removeItem('elevenlabs_config');
    return { apiKey: '', voiceId: 'EXAVITQu4vr4xnSDxMaL', isConfigured: false };
  },

  clearElevenLabs: async () => {
    const wsId = getCurrentWorkspaceId();
    const { data: existing } = await supabase.from('integrations_config').select('ai_config').eq('workspace_id', wsId).maybeSingle();
    const aiConfig = { ...((existing as any)?.ai_config || {}), elevenlabs: { apiKey: '', voiceId: '', isConfigured: false } };
    await supabase.from('integrations_config' as any).upsert({
      id: wsId, workspace_id: wsId, ai_config: aiConfig, updated_at: new Date().toISOString(),
    } as any, { onConflict: 'id' });
    localStorage.removeItem(`elevenlabs_config_${wsId}`);
    localStorage.removeItem('elevenlabs_config'); // clean old global key
    logService.add('integrations', 'elevenlabs_cleared', 'ElevenLabs config cleared', {}, 'info');
  },

  maskSecret: (value: string): string => {
    if (!value || value.length < 8) return value ? '••••' : '';
    return '••••••••' + value.slice(-4);
  },

  clearSection: (section: 'whatsapp' | 'ai' | 'webhook' | 'ecommerce') => {
    const data = _getIntegrationData();
    const def = defaultIntegrationData();
    (data as any)[section] = (def as any)[section];
    if (section === 'whatsapp') data.whatsappStatus = 'not_configured';
    if (section === 'ai') data.aiStatus = 'not_configured';
    if (section === 'webhook') { data.webhookStatus = 'not_configured'; }
    if (section === 'ecommerce') data.ecommerceStatus = 'not_configured';
    data.updatedAt = new Date().toISOString();
    setDocument(`wf_integrations:${getCurrentWorkspaceId()}`, data);
    notify('wf_integrations');

    const dbColumn: Record<string, string> = { whatsapp: 'whatsapp_config', ai: 'ai_config', webhook: 'webhook_config', ecommerce: 'ecommerce_db_config' };
    void supabase.from('integrations_config' as any).upsert({
      id: getCurrentWorkspaceId(),
      workspace_id: getCurrentWorkspaceId(),
      [dbColumn[section]]: (def as any)[section],
      updated_at: data.updatedAt,
    } as any, { onConflict: 'id' });

    logService.add('integrations', 'config_cleared', `Integration "${section}" configuration cleared`, { section }, 'info');
  },
};

// ===================== SAVED REPLIES SERVICE =====================

export const savedReplyService = {
  subscribe: (callback: (replies: SavedReply[]) => void) => {
    const handler = () => callback(getCollection<SavedReply>('wf_saved_replies'));
    handler();
    return subscribe('wf_saved_replies', handler);
  },

  getAll: (): SavedReply[] => getCollection<SavedReply>('wf_saved_replies'),

  create: (data: Partial<SavedReply>): { ok: boolean; errors: string[]; id?: string } => {
    const errors: string[] = [];
    if (!data.title?.trim()) errors.push('Title is required');
    if (!data.body?.trim()) errors.push('Reply body is required');
    if (errors.length) return { ok: false, errors };

    const replies = getCollection<SavedReply>('wf_saved_replies');
    const now = new Date().toISOString();
    const reply: SavedReply = {
      id: generateId(),
      title: data.title!.trim(),
      body: data.body!.trim(),
      category: data.category || 'general',
      usageCount: 0,
      createdAt: now,
      updatedAt: now,
    };
    replies.push(reply);
    setCollection('wf_saved_replies', replies);
    notify('wf_saved_replies');
    void supabase.from('saved_replies' as any).upsert({ id: reply.id, title: reply.title, body: reply.body, category: reply.category, usage_count: reply.usageCount, created_at: reply.createdAt, updated_at: reply.updatedAt, workspace_id: getCurrentWorkspaceId() } as any, { onConflict: 'id' });
    logService.add('settings', 'reply_created', `Saved reply "${reply.title}" created`, { replyId: reply.id }, 'success');
    return { ok: true, errors: [], id: reply.id };
  },

  update: (id: string, data: Partial<SavedReply>) => {
    const replies = getCollection<SavedReply>('wf_saved_replies');
    const idx = replies.findIndex(r => r.id === id);
    if (idx !== -1) {
      replies[idx] = { ...replies[idx], ...data, updatedAt: new Date().toISOString() };
      setCollection('wf_saved_replies', replies);
      notify('wf_saved_replies');
      const r = replies[idx];
      void supabase.from('saved_replies' as any).upsert({ id: r.id, title: r.title, body: r.body, category: r.category, usage_count: r.usageCount, updated_at: r.updatedAt } as any, { onConflict: 'id' });
    }
  },

  incrementUsage: (id: string) => {
    const replies = getCollection<SavedReply>('wf_saved_replies');
    const idx = replies.findIndex(r => r.id === id);
    if (idx !== -1) {
      replies[idx].usageCount = (replies[idx].usageCount || 0) + 1;
      setCollection('wf_saved_replies', replies);
      notify('wf_saved_replies');
    }
  },

  delete: (id: string) => {
    const replies = getCollection<SavedReply>('wf_saved_replies');
    const reply = replies.find(r => r.id === id);
    setCollection('wf_saved_replies', replies.filter(r => r.id !== id));
    notify('wf_saved_replies');
    void supabase.from('saved_replies' as any).delete().eq('id', id);
    logService.add('settings', 'reply_deleted', `Saved reply "${reply?.title || id}" deleted`, { replyId: id }, 'info');
  },
};

// ===================== BLACKLIST SERVICE =====================

export const blacklistService = {
  subscribe: (callback: (entries: BlacklistEntry[]) => void) => {
    const handler = () => callback(getCollection<BlacklistEntry>('wf_blacklist'));
    handler();
    return subscribe('wf_blacklist', handler);
  },

  getAll: (): BlacklistEntry[] => getCollection<BlacklistEntry>('wf_blacklist'),

  isBlacklisted: (phone: string): boolean =>
    getCollection<BlacklistEntry>('wf_blacklist').some(e => e.phoneNumber === phone),

  add: (phoneNumber: string, reason?: string): { ok: boolean; errors: string[] } => {
    if (!phoneNumber.trim()) return { ok: false, errors: ['Phone number is required'] };
    const entries = getCollection<BlacklistEntry>('wf_blacklist');
    if (entries.some(e => e.phoneNumber === phoneNumber)) return { ok: false, errors: ['This number is already blacklisted'] };
    const now = new Date().toISOString();
    const entry: BlacklistEntry = { id: generateId(), phoneNumber: phoneNumber.trim(), reason: reason || 'Manual block', blockedAt: now, createdAt: now };
    entries.push(entry);
    setCollection('wf_blacklist', entries);
    notify('wf_blacklist');
    void supabase.from('blacklist' as any).upsert({ id: entry.id, phone_number: entry.phoneNumber, reason: entry.reason || null, blocked_at: entry.blockedAt, created_at: entry.createdAt, workspace_id: getCurrentWorkspaceId() } as any, { onConflict: 'id' });
    logService.add('settings', 'number_blocked', `Phone ${phoneNumber} blacklisted: ${reason || 'Manual block'}`, { phoneNumber }, 'info');
    return { ok: true, errors: [] };
  },

  remove: (id: string) => {
    const entries = getCollection<BlacklistEntry>('wf_blacklist');
    const entry = entries.find(e => e.id === id);
    setCollection('wf_blacklist', entries.filter(e => e.id !== id));
    notify('wf_blacklist');
    void supabase.from('blacklist' as any).delete().eq('id', id);
    logService.add('settings', 'number_unblocked', `Phone ${entry?.phoneNumber || id} unblocked`, { phoneNumber: entry?.phoneNumber }, 'info');
  },
};

// ===================== ANALYTICS SERVICE =====================

export const analyticsService = {
  // ---- Date range helper ----
  _getRange: (dateRange?: { start: Date; end: Date } | null): { start: Date; end: Date } | null => {
    return dateRange || null;
  },

  _inRange: (dateStr: string | undefined, range: { start: Date; end: Date } | null): boolean => {
    if (!range || !dateStr) return true; // no range = include all
    const d = new Date(dateStr);
    return d >= range.start && d <= range.end;
  },

  // ---- Core dashboard metrics ----
  getDashboardMetrics: (dateRange?: { start: Date; end: Date } | null) => {
    const range = analyticsService._getRange(dateRange);
    const allContacts = contactService.getAll();
    const allConversations = conversationService.getAll();

    // Total contacts (created within range)
    const contacts = range
      ? allContacts.filter(c => analyticsService._inRange(c.createdAt, range))
      : allContacts;
    const totalContacts = contacts.length;

    // Active conversations: not closed AND with lastMessageAt in range
    const activeConversations = allConversations.filter(c => {
      if (c.status === 'closed') return false;
      if (range) return analyticsService._inRange(c.lastMessageAt || c.createdAt, range);
      return true;
    }).length;

    // Messages sent (outbound) within range
    let messagesSent = 0;
    allConversations.forEach(conv => {
      const msgs = getCollection<Message>(`wf_messages_${conv.id}`);
      msgs.forEach(m => {
        if (m.direction === 'outbound' && analyticsService._inRange(m.createdAt, range)) {
          messagesSent++;
        }
      });
    });

    // Conversion rate: placeholder — real value computed async via getConversionRateFromDB
    const conversionRate = 0;

    // Revenue: sum orderAmount for delivered/confirmed/shipped contacts in range
    const revenueContacts = range
      ? allContacts.filter(c => analyticsService._inRange(c.orderCreatedAt || c.lastOrderUpdateAt || c.createdAt, range))
      : allContacts;
    const revenue = revenueContacts.reduce((sum, c) => {
      if (c.orderStatus === 'delivered' || c.orderStatus === 'confirmed' || c.orderStatus === 'shipped') {
        return sum + (c.orderAmount || 0);
      }
      return sum;
    }, 0);

    // Unread
    const unreadConversations = conversationService.countWithUnread();

    return {
      totalContacts,
      activeConversations,
      messagesSent,
      conversionRate,
      revenue,
      unreadConversations,
      totalContactsAll: allContacts.length,
      customersAll: 0,
    };
  },

  // ---- Conversion rate from order_requests DB (async) ----
  getConversionRateFromDB: async (dateRange?: { start: Date; end: Date } | null): Promise<{ rate: number; completed: number; totalConversations: number }> => {
    try {
      // Count completed order_requests
      const wsId = getCurrentWorkspaceId();
      let orderQuery = supabase.from('order_requests').select('id, order_stage, status, created_at').eq('workspace_id', wsId);
      if (dateRange) {
        orderQuery = orderQuery.gte('created_at', dateRange.start.toISOString()).lte('created_at', dateRange.end.toISOString());
      }
      const { data: orders } = await orderQuery;
      // Count as converted: reached completed stage OR has confirmed/cancelled/fulfilled status (implies went through completed)
      const completed = (orders || []).filter(o =>
        o.order_stage === 'completed' || o.status === 'confirmed' || o.status === 'cancelled' || o.status === 'fulfilled'
      ).length;

      // Count total conversations
      let convQuery = supabase.from('conversations').select('id', { count: 'exact', head: true }).eq('workspace_id', wsId);
      if (dateRange) {
        convQuery = convQuery.gte('created_at', dateRange.start.toISOString()).lte('created_at', dateRange.end.toISOString());
      }
      const { count } = await convQuery;
      const totalConversations = count || 0;

      const rate = totalConversations > 0 ? parseFloat(((completed / totalConversations) * 100).toFixed(1)) : 0;
      return { rate, completed, totalConversations };
    } catch (err) {
      console.error('[ConversionRate] DB query failed:', err);
      return { rate: 0, completed: 0, totalConversations: 0 };
    }
  },

  // ---- Messaging performance chart ----
  getMessagingPerformance: (dateRange?: { start: Date; end: Date } | null) => {
    const range = analyticsService._getRange(dateRange);
    const now = new Date();
    // Determine days to show based on range
    let days = 7;
    if (range) {
      days = Math.max(1, Math.min(60, Math.ceil((range.end.getTime() - range.start.getTime()) / (86400000))));
    }
    const startDate = range ? range.start : new Date(now.getTime() - (days - 1) * 86400000);

    const result: { name: string; delivered: number; failed: number; sent: number }[] = [];
    const conversations = conversationService.getAll();

    for (let i = 0; i < days; i++) {
      const day = new Date(startDate.getTime() + i * 86400000);
      const dayStart = new Date(day); dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(day); dayEnd.setHours(23, 59, 59, 999);

      let delivered = 0, failed = 0, sent = 0;
      conversations.forEach(conv => {
        const msgs = getCollection<Message>(`wf_messages_${conv.id}`);
        msgs.forEach(m => {
          if (m.direction !== 'outbound') return;
          const d = new Date(m.createdAt);
          if (d >= dayStart && d <= dayEnd) {
            sent++;
            if (m.deliveryState === 'delivered' || m.deliveryState === 'read' || m.deliveryState === 'local_only') delivered++;
            if (m.deliveryState === 'failed') failed++;
          }
        });
      });

      const dayLabel = days <= 7
        ? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][day.getDay()]
        : `${day.getMonth() + 1}/${day.getDate()}`;
      result.push({ name: dayLabel, delivered, failed, sent });
    }
    return result;
  },

  // ---- Recent activity ----
  getRecentActivity: (dateRange?: { start: Date; end: Date } | null, limit: number = 8): SystemLog[] => {
    const range = analyticsService._getRange(dateRange);
    let logs = getCollection<SystemLog>('wf_logs')
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    if (range) {
      logs = logs.filter(l => analyticsService._inRange(l.createdAt, range));
    }
    return logs.slice(0, limit);
  },

  // ---- Top automations ----
  getTopAutomations: (dateRange?: { start: Date; end: Date } | null, limit: number = 5) => {
    const range = analyticsService._getRange(dateRange);
    const automations = automationService.getAll();
    const allRuns = automationRunService.getAll();

    return automations.map(flow => {
      let runs = allRuns.filter(r => r.automationId === flow.id);
      if (range) {
        runs = runs.filter(r => analyticsService._inRange(r.startedAt || r.createdAt, range));
      }
      const triggered = runs.length;
      const completed = runs.filter(r => r.status === 'completed').length;
      const failed = runs.filter(r => r.status === 'failed').length;
      const successRate = triggered > 0 ? (completed / triggered) * 100 : 0;
      const lastRun = runs.length > 0 ? runs.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0] : null;
      return { ...flow, triggered, completed, failed, successRate, lastRunDate: lastRun?.createdAt };
    }).sort((a, b) => b.triggered - a.triggered).slice(0, limit);
  },

  // ---- Revenue metrics (sync fallback from contacts) ----
  getRevenueMetrics: (dateRange?: { start: Date; end: Date } | null) => {
    const range = analyticsService._getRange(dateRange);
    const contacts = contactService.getAll();
    const filtered = range
      ? contacts.filter(c => analyticsService._inRange(c.orderCreatedAt || c.lastOrderUpdateAt || c.createdAt, range))
      : contacts;

    const revenue = filtered.reduce((sum, c) => {
      if (c.orderStatus === 'delivered' || c.orderStatus === 'confirmed' || c.orderStatus === 'shipped') {
        return sum + (c.orderAmount || 0);
      }
      return sum;
    }, 0);

    const orderCount = filtered.filter(c => c.orderStatus).length;
    const deliveredCount = filtered.filter(c => c.orderStatus === 'delivered').length;

    const ws = _getWorkspaceSettings();
    return { revenue, orderCount, deliveredCount, currency: ws.general.defaultCurrency || 'USD' };
  },

  // ---- Revenue metrics from order_requests DB (async, primary) ----
  getRevenueMetricsFromDB: async (dateRange?: { start: Date; end: Date } | null): Promise<{ revenue: number; orderCount: number; confirmedCount: number; currency: string }> => {
    try {
      const wsId2 = getCurrentWorkspaceId();
      let query = supabase.from('order_requests').select('id, status, quantity, product_id, created_at').eq('workspace_id', wsId2);
      if (dateRange) {
        query = query.gte('created_at', dateRange.start.toISOString()).lte('created_at', dateRange.end.toISOString());
      }
      const { data: orders, error } = await query;
      if (error || !orders) return { revenue: 0, orderCount: 0, confirmedCount: 0, currency: 'USD' };

      const confirmedOrders = orders.filter(o => o.status === 'confirmed' || o.status === 'fulfilled');
      const productIds = [...new Set(confirmedOrders.map(o => o.product_id).filter(Boolean))] as string[];

      let productPrices: Record<string, number> = {};
      if (productIds.length > 0) {
        const { data: products } = await supabase.from('products').select('id, price').in('id', productIds);
        if (products) {
          products.forEach(p => { productPrices[p.id] = p.price || 0; });
        }
      }

      const revenue = confirmedOrders.reduce((sum, o) => {
        const price = o.product_id ? (productPrices[o.product_id] || 0) : 0;
        return sum + price * (o.quantity || 1);
      }, 0);

      const ws = _getWorkspaceSettings();
      return {
        revenue,
        orderCount: orders.length,
        confirmedCount: confirmedOrders.length,
        currency: ws.general.defaultCurrency || 'USD',
      };
    } catch (err) {
      console.error('[Revenue] DB query failed:', err);
      const ws = _getWorkspaceSettings();
      return { revenue: 0, orderCount: 0, confirmedCount: 0, currency: ws.general.defaultCurrency || 'USD' };
    }
  },

  // ---- Orders per day from order_requests DB ----
  getOrdersPerDayFromDB: async (dateRange?: { start: Date; end: Date } | null): Promise<{ name: string; total: number; completed: number }[]> => {
    try {
      const wsId2 = getCurrentWorkspaceId();
      const range = dateRange || { start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), end: new Date() };
      let query = supabase.from('order_requests').select('id, status, created_at').eq('workspace_id', wsId2);
      query = query.gte('created_at', range.start.toISOString()).lte('created_at', range.end.toISOString());
      const { data: orders, error } = await query;
      if (error || !orders) return [];

      // Build day buckets
      const days: { name: string; key: string; total: number; completed: number }[] = [];
      const startMs = new Date(range.start).setHours(0, 0, 0, 0);
      const endMs = new Date(range.end).setHours(0, 0, 0, 0);
      const dayMs = 24 * 60 * 60 * 1000;
      const totalDays = Math.min(60, Math.max(1, Math.round((endMs - startMs) / dayMs) + 1));
      for (let i = 0; i < totalDays; i++) {
        const d = new Date(startMs + i * dayMs);
        const key = d.toISOString().slice(0, 10);
        const name = `${d.getMonth() + 1}/${d.getDate()}`;
        days.push({ name, key, total: 0, completed: 0 });
      }
      const map = new Map(days.map(d => [d.key, d]));
      orders.forEach(o => {
        const key = new Date(o.created_at).toISOString().slice(0, 10);
        const bucket = map.get(key);
        if (bucket) {
          bucket.total += 1;
          if (o.status === 'fulfilled') bucket.completed += 1;
        }
      });
      return days.map(({ name, total, completed }) => ({ name, total, completed }));
    } catch (err) {
      console.error('[OrdersPerDay] DB query failed:', err);
      return [];
    }
  },

  // ---- Average response time ----
  getAverageResponseTime: (dateRange?: { start: Date; end: Date } | null): string => {
    const range = analyticsService._getRange(dateRange);
    const conversations = conversationService.getAll();
    const responseTimes: number[] = [];

    conversations.forEach(conv => {
      const msgs = messageService.getByConversation(conv.id);
      for (let i = 0; i < msgs.length - 1; i++) {
        if (msgs[i].direction === 'inbound' && msgs[i + 1].direction === 'outbound') {
          if (range && !analyticsService._inRange(msgs[i].createdAt, range)) continue;
          const diff = new Date(msgs[i + 1].createdAt).getTime() - new Date(msgs[i].createdAt).getTime();
          if (diff > 0 && diff < 24 * 60 * 60 * 1000) responseTimes.push(diff);
        }
      }
    });

    if (responseTimes.length === 0) return 'N/A';
    const avg = responseTimes.reduce((s, v) => s + v, 0) / responseTimes.length;
    const minutes = Math.round(avg / 60000);
    if (minutes < 1) return '< 1m';
    return minutes < 60 ? `${minutes}m` : `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  },

  // ---- Legacy compatibility ----
  getStats: () => analyticsService.getDashboardMetrics(),
  computeAvgResponseTime: () => analyticsService.getAverageResponseTime(),

  getMessagesByDay: (days: number = 7, dateRange?: { start: Date; end: Date } | null) => {
    const range = dateRange || (() => {
      const now = new Date();
      const start = new Date(now); start.setDate(start.getDate() - (days - 1)); start.setHours(0, 0, 0, 0);
      const end = new Date(now); end.setHours(23, 59, 59, 999);
      return { start, end };
    })();
    const conversations = conversationService.getAll();
    const dayCount = Math.max(1, Math.ceil((range.end.getTime() - range.start.getTime()) / 86400000));
    const result: { name: string; outbound: number; inbound: number }[] = [];
    for (let i = 0; i < dayCount; i++) {
      const day = new Date(range.start.getTime() + i * 86400000);
      const dayStart = new Date(day); dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(day); dayEnd.setHours(23, 59, 59, 999);
      let outbound = 0, inbound = 0;
      conversations.forEach(conv => {
        const msgs = getCollection<Message>(`wf_messages_${conv.id}`);
        msgs.forEach(m => {
          const d = new Date(m.createdAt);
          if (d >= dayStart && d <= dayEnd) {
            if (m.direction === 'outbound') outbound++;
            else if (m.direction === 'inbound') inbound++;
          }
        });
      });
      const dayLabel = dayCount <= 7
        ? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][day.getDay()]
        : `${day.getMonth() + 1}/${day.getDate()}`;
      result.push({ name: dayLabel, outbound, inbound });
    }
    return result;
  },

  getContactGrowth: (days: number = 30, dateRange?: { start: Date; end: Date } | null): { name: string; contacts: number }[] => {
    const contacts = contactService.getAll();
    const now = new Date();
    const range = dateRange || (() => {
      const start = new Date(now); start.setDate(start.getDate() - (days - 1)); start.setHours(0, 0, 0, 0);
      const end = new Date(now); end.setHours(23, 59, 59, 999);
      return { start, end };
    })();
    const dayCount = Math.max(1, Math.ceil((range.end.getTime() - range.start.getTime()) / 86400000));
    const result: { name: string; contacts: number }[] = [];
    for (let i = 0; i < dayCount; i++) {
      const day = new Date(range.start.getTime() + i * 86400000);
      const end = new Date(day); end.setHours(23, 59, 59, 999);
      const count = contacts.filter(c => new Date(c.createdAt) <= end).length;
      result.push({ name: `${day.getMonth() + 1}/${day.getDate()}`, contacts: count });
    }
    return result;
  },

  getAgentPerformance: (dateRange?: { start: Date; end: Date } | null): { name: string; conversations: number; messages: number }[] => {
    const range = analyticsService._getRange(dateRange);
    const team = settingsService.getTeam();
    const conversations = conversationService.getAll();
    return team.map(member => {
      const assignedConvs = conversations.filter(c => c.assignedAgentId === member.id);
      let msgCount = 0;
      assignedConvs.forEach(conv => {
        const msgs = messageService.getByConversation(conv.id);
        msgs.forEach(m => {
          if (m.direction === 'outbound') {
            if (!range || analyticsService._inRange(m.createdAt, range)) msgCount++;
          }
        });
      });
      return { name: member.name, conversations: assignedConvs.length, messages: msgCount };
    }).filter(a => a.conversations > 0 || a.messages > 0);
  },

  getCityBreakdown: (dateRange?: { start: Date; end: Date } | null): { name: string; value: number }[] => {
    const range = analyticsService._getRange(dateRange);
    let contacts = contactService.getAll();
    if (range) contacts = contacts.filter(c => analyticsService._inRange(c.createdAt, range));
    const cityMap: Record<string, number> = {};
    contacts.forEach(c => {
      const city = c.city || 'Unknown';
      cityMap[city] = (cityMap[city] || 0) + 1;
    });
    return Object.entries(cityMap).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 8);
  },

  getOrderStatusDistribution: (dateRange?: { start: Date; end: Date } | null): Record<string, number> => {
    const range = analyticsService._getRange(dateRange);
    let contacts = contactService.getAll();
    if (range) contacts = contacts.filter(c => analyticsService._inRange(c.orderCreatedAt || c.createdAt, range));
    const statuses: Record<string, number> = {};
    contacts.forEach(c => { if (c.orderStatus) statuses[c.orderStatus] = (statuses[c.orderStatus] || 0) + 1; });
    return statuses;
  },

  getDeliverySuccessRate: (dateRange?: { start: Date; end: Date } | null): string => {
    const range = analyticsService._getRange(dateRange);
    const conversations = conversationService.getAll();
    let total = 0, delivered = 0;
    conversations.forEach(conv => {
      getCollection<Message>(`wf_messages_${conv.id}`).forEach(m => {
        if (m.direction !== 'outbound') return;
        if (range && !analyticsService._inRange(m.createdAt, range)) return;
        total++;
        if (m.deliveryState === 'delivered' || m.deliveryState === 'read' || m.deliveryState === 'local_only') delivered++;
      });
    });
    return total === 0 ? 'N/A' : `${Math.round((delivered / total) * 100)}%`;
  },

  getActiveAutomationsCount: (): number => automationService.getAll().filter(a => a.status === 'active').length,

  getLeadStatusDistribution: (dateRange?: { start: Date; end: Date } | null): Record<string, number> => {
    const range = analyticsService._getRange(dateRange);
    let contacts = contactService.getAll();
    if (range) contacts = contacts.filter(c => analyticsService._inRange(c.createdAt, range));
    const statuses: Record<string, number> = {};
    contacts.forEach(c => { statuses[c.leadStatus] = (statuses[c.leadStatus] || 0) + 1; });
    return statuses;
  },

  getCampaignPerformance: (dateRange?: { start: Date; end: Date } | null) => {
    const range = analyticsService._getRange(dateRange);
    let campaigns = campaignService.getAll();
    if (range) campaigns = campaigns.filter(c => analyticsService._inRange(c.createdAt, range));
    return {
      totalCampaigns: campaigns.length,
      totalSent: campaigns.reduce((s, c) => s + c.sentCount, 0),
      totalDelivered: campaigns.reduce((s, c) => s + c.deliveredCount, 0),
      totalFailed: campaigns.reduce((s, c) => s + c.failedCount, 0),
      totalReplied: campaigns.reduce((s, c) => s + c.repliedCount, 0),
    };
  },

  subscribeToStats: (callback: (stats: any) => void) => {
    const handler = () => callback(analyticsService.getDashboardMetrics());
    const unsub1 = subscribe('wf_contacts', handler);
    const unsub2 = subscribe('wf_automations', handler);
    const unsub3 = subscribe('wf_campaigns', handler);
    const unsub4 = subscribe('wf_logs', handler);
    const unsub5 = subscribe('wf_conversations', handler);
    return () => { unsub1(); unsub2(); unsub3(); unsub4(); unsub5(); };
  },
};

// ===================== NOTIFICATION SERVICE =====================

import type { AppNotification, GlobalSearchResult } from '../types';

export const notificationService = {
  subscribe: (callback: (notifications: AppNotification[]) => void) => {
    const handler = () => callback(
      getCollection<AppNotification>('wf_notifications')
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 50)
    );
    handler();
    return subscribe('wf_notifications', handler);
  },

  getAll: (): AppNotification[] =>
    getCollection<AppNotification>('wf_notifications')
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),

  getUnreadCount: (): number =>
    getCollection<AppNotification>('wf_notifications').filter(n => !n.isRead).length,

  add: (type: AppNotification['type'], title: string, message: string, module: string, relatedId?: string): string => {
    // Check if notifications enabled
    const ws = getDocument<WorkspaceSettings>('wf_settings_workspace');
    if (ws && !ws.preferences.notificationsEnabled) return '';

    const notifications = getCollection<AppNotification>('wf_notifications');
    const entry: AppNotification = {
      id: generateId(),
      type, title, message, module, relatedId,
      isRead: false,
      createdAt: new Date().toISOString(),
    };
    notifications.push(entry);
    setCollection('wf_notifications', notifications.slice(-200));
    notify('wf_notifications');
    void supabase.from('notifications' as any).upsert({ id: entry.id, type: entry.type, title: entry.title, message: entry.message, module: entry.module, related_id: entry.relatedId || null, is_read: false, created_at: entry.createdAt } as any, { onConflict: 'id' });
    return entry.id;
  },

  markAsRead: (id: string) => {
    const notifications = getCollection<AppNotification>('wf_notifications');
    const idx = notifications.findIndex(n => n.id === id);
    if (idx !== -1) {
      notifications[idx].isRead = true;
      setCollection('wf_notifications', notifications);
      notify('wf_notifications');
      void supabase.from('notifications' as any).update({ is_read: true } as any).eq('id', id);
    }
  },

  markAllAsRead: () => {
    const notifications = getCollection<AppNotification>('wf_notifications');
    const unreadIds = notifications.filter(n => !n.isRead).map(n => n.id);
    notifications.forEach(n => { n.isRead = true; });
    setCollection('wf_notifications', notifications);
    notify('wf_notifications');
    if (unreadIds.length > 0) {
      void supabase.from('notifications' as any).update({ is_read: true } as any).in('id', unreadIds);
    }
  },

  clear: () => {
    const ids = getCollection<AppNotification>('wf_notifications').map(n => n.id);
    setCollection('wf_notifications', []);
    notify('wf_notifications');
    if (ids.length > 0) {
      void supabase.from('notifications' as any).delete().in('id', ids);
    }
  },
};

// ---- Auto-generate notifications from log events ----
// Patch logService.add to also create notifications for important events
const _originalLogAdd = logService.add;
logService.add = (module: string, action: string, message: string, metadata: Record<string, any> = {}, level: LogLevel = 'info', entityType?: string, entityId?: string): string => {
  const id = _originalLogAdd(module, action, message, metadata, level, entityType, entityId);

  // Create notification for important events
  const notifMap: Record<string, { type: AppNotification['type']; title: string }> = {
    'message_received': { type: 'message', title: 'New Message' },
    'whatsapp_message_received': { type: 'message', title: 'New WhatsApp Message' },
    'create': module === 'contacts' ? { type: 'contact', title: 'New Contact' } : (null as any),
    'campaign_sent': { type: 'campaign', title: 'Campaign Sent' },
    'campaign_started': { type: 'campaign', title: 'Campaign Started' },
    'automation_completed': { type: 'automation', title: 'Automation Completed' },
    'automation_failed': { type: 'automation', title: 'Automation Failed' },
    'ai_auto_reply_sent': { type: 'automation', title: 'AI Auto-Reply' },
    'whatsapp_message_failed': { type: 'error', title: 'Message Failed' },
  };

  const mapping = notifMap[action];
  if (mapping) {
    notificationService.add(mapping.type, mapping.title, message, module, entityId);
  }
  // System errors always notify
  if (level === 'error' && !mapping) {
    notificationService.add('error', 'System Error', message, module, entityId);
  }

  return id;
};

// ===================== GLOBAL SEARCH SERVICE =====================

export const globalSearchService = {
  search: (query: string, limit: number = 5): GlobalSearchResult[] => {
    if (!query || query.trim().length < 2) return [];
    const q = query.toLowerCase().trim();
    const results: GlobalSearchResult[] = [];

    // Search contacts
    const contacts = getCollection<Contact>('wf_contacts');
    contacts.forEach(c => {
      if (
        c.fullName.toLowerCase().includes(q) ||
        c.phoneNumber.includes(q) ||
        (c.email && c.email.toLowerCase().includes(q)) ||
        (c.city && c.city.toLowerCase().includes(q))
      ) {
        results.push({
          id: c.id, type: 'contact', module: 'contacts',
          title: c.fullName,
          subtitle: `${c.phoneNumber}${c.city ? ' · ' + c.city : ''}`,
          relatedId: c.id,
        });
      }
    });

    // Search conversations (by message content)
    const conversations = getCollection<Conversation>('wf_conversations');
    conversations.forEach(conv => {
      const contact = contacts.find(c => c.id === conv.contactId);
      const msgs = getCollection<Message>(`wf_messages_${conv.id}`);
      const matchMsg = msgs.find(m => m.body.toLowerCase().includes(q));
      if (matchMsg) {
        results.push({
          id: conv.id, type: 'conversation', module: 'inbox',
          title: contact?.fullName || 'Unknown',
          subtitle: `"${matchMsg.body.substring(0, 60)}..."`,
          relatedId: conv.contactId,
        });
      }
    });

    // Search campaigns
    const campaigns = getCollection<Campaign>('wf_campaigns');
    campaigns.forEach(c => {
      if (c.name.toLowerCase().includes(q)) {
        results.push({
          id: c.id, type: 'campaign', module: 'campaigns',
          title: c.name,
          subtitle: `${c.status} · ${c.totalRecipients} recipients`,
        });
      }
    });

    // Search templates
    const templates = getCollection<Template>('wf_templates');
    templates.forEach(t => {
      if (t.name.toLowerCase().includes(q) || t.bodyText.toLowerCase().includes(q)) {
        results.push({
          id: t.id, type: 'template', module: 'templates',
          title: t.name,
          subtitle: `${t.category} · ${t.status}`,
        });
      }
    });

    // Search automations
    const automations = getCollection<AutomationFlow>('wf_automations');
    automations.forEach(a => {
      if (a.name.toLowerCase().includes(q) || (a.description && a.description.toLowerCase().includes(q))) {
        results.push({
          id: a.id, type: 'automation', module: 'automations',
          title: a.name,
          subtitle: `${a.status} · ${a.triggerType}`,
        });
      }
    });

    // Limit per type then flatten
    const grouped: Record<string, GlobalSearchResult[]> = {};
    results.forEach(r => {
      if (!grouped[r.type]) grouped[r.type] = [];
      if (grouped[r.type].length < limit) grouped[r.type].push(r);
    });

    return Object.values(grouped).flat();
  },
};

// ===================== SHOPIFY PRODUCT SERVICE =====================

export interface ShopifyProductVariant {
  id: string;
  title: string;
  price: string;
  compareAtPrice: string | null;
  sku: string;
  inventoryQuantity: number;
  option1: string | null;
  option2: string | null;
  option3: string | null;
  weight: number;
  weightUnit: string;
}

export interface ShopifyProduct {
  id: string;
  title: string;
  description: string;
  vendor: string;
  productType: string;
  handle: string;
  status: string;
  tags: string[];
  images: string[];
  variants: ShopifyProductVariant[];
  options: { name: string; values: string[] }[];
  createdAt: string;
  updatedAt: string;
}

export interface ShopifySyncState {
  products: ShopifyProduct[];
  lastSyncAt: string | null;
  lastSyncCount: number;
  lastSyncError: string | null;
}

const SHOPIFY_PRODUCTS_KEY = 'wf_shopify_products';

export const shopifyProductService = {
  getState: (): ShopifySyncState => {
    return getDocument<ShopifySyncState>(SHOPIFY_PRODUCTS_KEY) || {
      products: [],
      lastSyncAt: null,
      lastSyncCount: 0,
      lastSyncError: null,
    };
  },

  saveState: (state: ShopifySyncState): void => {
    setDocument(SHOPIFY_PRODUCTS_KEY, state);
    notify(SHOPIFY_PRODUCTS_KEY);
  },

  subscribe: (callback: (state: ShopifySyncState) => void) => {
    const handler = () => callback(shopifyProductService.getState());
    handler();
    return subscribe(SHOPIFY_PRODUCTS_KEY, handler);
  },

  getAll: (): ShopifyProduct[] => shopifyProductService.getState().products,

  getById: (id: string): ShopifyProduct | undefined =>
    shopifyProductService.getState().products.find(p => p.id === id),

  search: (query: string): ShopifyProduct[] => {
    const q = query.toLowerCase();
    return shopifyProductService.getState().products.filter(p =>
      p.title.toLowerCase().includes(q) ||
      p.description.toLowerCase().includes(q) ||
      p.vendor.toLowerCase().includes(q) ||
      p.tags.some(t => t.toLowerCase().includes(q))
    );
  },

  /** Build a text summary of products for AI context injection */
  getAIContext: (): string => {
    const products = shopifyProductService.getAll();
    if (products.length === 0) return '';
    const lines = products.map(p => {
      const prices = p.variants.map(v => v.price).filter(Boolean);
      const minPrice = prices.length ? Math.min(...prices.map(Number)) : null;
      const maxPrice = prices.length ? Math.max(...prices.map(Number)) : null;
      const priceStr = minPrice !== null
        ? (minPrice === maxPrice ? `${minPrice}` : `${minPrice}-${maxPrice}`)
        : 'N/A';
      const stock = p.variants.reduce((sum, v) => sum + (v.inventoryQuantity || 0), 0);
      const variantInfo = p.variants.length > 1
        ? ` | Variants: ${p.variants.map(v => v.title).join(', ')}`
        : '';
      return `- ${p.title} | Price: ${priceStr} | Stock: ${stock}${variantInfo}${p.description ? ` | ${p.description.slice(0, 120)}` : ''}`;
    });
    return `=== SHOPIFY PRODUCT CATALOG (${products.length} products) ===\n${lines.join('\n')}`;
  },

  clear: (): void => {
    localStorage.removeItem(SHOPIFY_PRODUCTS_KEY);
    notify(SHOPIFY_PRODUCTS_KEY);
  },
};
